import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Card, CardContent } from '../ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { seminarApi, bookingApi, invoiceApi, templateApi, referentApi, settingsApi } from '../../lib/api';
import { EmailEditor } from '../common/EmailEditor';
import { ParticipantMailComposer } from '../common/ParticipantMailComposer';
import { ParticipantListExportDialog } from '../common/ParticipantListExportDialog';
import { CancelCourseDialog } from '../dialogs/CancelCourseDialog';
import { InHouseDialog } from '../dialogs/InHouseDialog';
import { DeleteImpactNotice } from '../dialogs/DeleteImpactNotice';
import { formatCurrency, downloadBlob } from '../../lib/utils';
import { toast } from 'sonner';
import {
    ArrowLeft, Save, Loader2, Trash2, Eye, EyeOff, CalendarPlus, X, Tag,
    Users, CheckCircle, XCircle, Receipt, UserCheck, Award,
    Pencil, Printer, Mail, Clock, ArrowUpCircle, Trash, AlertTriangle,
    AlertCircle, HeartPulse, Ban, Copy, Building2, RefreshCw,
} from 'lucide-react';

const emptyDate = { date: '', time_start: '09:00', time_end: '17:00', format: 'praesenz', meeting_link: '' };
const emptyDiscount = { name: '', valid_from: '', valid_until: '', discount_value: 0, discount_type: 'euro', description: '' };


/**
 * Multi-Select-Chip-Picker für Referenten. Lädt die aktiven Einträge aus
 * der Stammdatenverwaltung und erlaubt Auswahl per Dropdown. Ausgewählte
 * Referenten erscheinen als entfernbare Chips.
 */
function ReferentMultiSelect({ selectedIds = [], onChange, fallbackText = '' }) {
    const [all, setAll] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    useEffect(() => {
        referentApi.list(true).then(r => setAll(r.data || [])).catch(() => setAll([]));
    }, []);
    const selected = selectedIds.map(id => all.find(r => r.id === id)).filter(Boolean);
    const available = all.filter(r => !selectedIds.includes(r.id));

    const add = (id) => { onChange([...selectedIds, id]); setPickerOpen(false); };
    const removeId = (id) => onChange(selectedIds.filter(x => x !== id));

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 min-h-[42px] p-2 border rounded-md bg-white">
                {selected.length === 0 ? (
                    <span className="text-sm text-muted-foreground italic">
                        {fallbackText ? `Legacy-Freitext: „${fallbackText}" (wird übernommen, solange keine Auswahl getroffen wird)` : 'Noch keine Referenten ausgewählt'}
                    </span>
                ) : selected.map(r => (
                    <span key={r.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-2.5 py-1 text-xs" data-testid={`seminar-referent-chip-${r.id}`}>
                        {r.display_name}
                        <button type="button" onClick={() => removeId(r.id)} className="hover:text-blue-950 ml-0.5" aria-label={`${r.display_name} entfernen`}>×</button>
                    </span>
                ))}
            </div>
            {pickerOpen ? (
                <div className="border rounded-md bg-white shadow-sm max-h-52 overflow-y-auto">
                    {available.length === 0 ? (
                        <p className="text-xs p-3 text-muted-foreground">Keine weiteren verfügbar. Neue Referenten unter <em>Referenten</em> im Admin-Menü anlegen.</p>
                    ) : available.map(r => (
                        <button key={r.id} type="button" onClick={() => add(r.id)}
                                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                data-testid={`pick-referent-${r.id}`}>
                            <span className="font-medium">{r.display_name}</span>
                            {r.email && <span className="text-xs text-muted-foreground ml-2">{r.email}</span>}
                        </button>
                    ))}
                </div>
            ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} data-testid="open-referent-picker">
                    + Referent hinzufügen
                </Button>
            )}
        </div>
    );
}


const emptyForm = {
    title: '', subtitle: '', course_number: '', description: '',
    dates: [{ ...emptyDate }], date: '', time_start: '09:00', time_end: '17:00',
    date_mode: 'individual', date_range_start: '', date_range_end: '',
    location: '', instructor_name: '', referent_ids: [], ue_count: 8,
    max_participants: 20, min_participants: 5, price: 0, discounts: [],
    external_link: '', organizer: 'BGPR-Akademie', organizer_type: 'bgpr',
    organizer_name: '', event_category: 'fortbildung', event_format: 'praesenz',
    training_type: '', training_license_type_id: null, requires_prerequisites: false,
    certificate_type: 'keine',
    certificate_content: '', is_published: false,
    is_first_aid: false,
    survey_link: '',
    registration_deadline_days: '',
    // In-House / Firmenfortbildung
    is_in_house: false,
    in_house_company_name: '',
    in_house_contact_person: '',
    in_house_email: '',
    in_house_phone: '',
    in_house_price_type: 'flat',
    in_house_flat_price: '',
    in_house_per_participant_price: '',
    in_house_max_participants: '',
};

const CATEGORY = { fortbildung: 'Fortbildung', weiterbildung: 'Weiterbildung', ausbildung: 'Ausbildung' };
const FORMAT = { praesenz: 'Präsenz', hybrid: 'Hybrid', online: 'Online' };
const TRAINING_TYPE = { vollausbildung: 'Vollausbildung (120 UE)', verkuerzt: 'Verkürzte Ausbildung' };
const CERT_TYPES = {
    teilnahmebescheinigung: 'Teilnahmebescheinigung',
    herzgruppenleiter_lizenz: 'Herzgruppenleiter-Lizenz',
    keine: 'Keine Bescheinigung',
};

function fmtDate(d) {
    if (!d) return '';
    try {
        if (d.includes('-')) { const p = d.split('-'); return `${p[2]}.${p[1]}.${p[0]}`; }
    } catch { /* */ }
    return d;
}

export default function AdminSeminarDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(isNew);
    const [formData, setFormData] = useState({ ...emptyForm, dates: [{ ...emptyDate }] });
    // Tracks the persisted (server-side) value of is_first_aid so we can detect
    // when the admin toggles it and warn that existing auto-records still hold
    // the old record_type — they need a "Nachweis-Typ angleichen" run.
    const originalIsFirstAidRef = useRef(null);
    const [activeTab, setActiveTab] = useState('details');
    const [searchParams, setSearchParams] = useSearchParams();

    // Akzeptiere ?tab=... aus der URL, damit man z. B. vom Teilnehmer-Detail
    // zurück direkt in die Teilnehmerliste springt.
    useEffect(() => {
        const urlTab = searchParams.get('tab');
        if (urlTab && urlTab !== activeTab) {
            setActiveTab(urlTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Wenn der Admin den Tab wechselt, URL synchronisieren — so landet man auch
    // nach einem Browser-Reload wieder im richtigen Tab.
    const handleTabChange = (next) => {
        setActiveTab(next);
        const p = new URLSearchParams(searchParams);
        if (next === 'details') p.delete('tab'); else p.set('tab', next);
        setSearchParams(p, { replace: true });
    };
    const [participants, setParticipants] = useState([]);
    const [loadingParticipants, setLoadingParticipants] = useState(false);
    const [courseConfirmed, setCourseConfirmed] = useState(false);
    const [isCancelled, setIsCancelled] = useState(false);
    const [cancellationReason, setCancellationReason] = useState('');
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [inHouseDialogOpen, setInHouseDialogOpen] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [licenseTypes, setLicenseTypes] = useState([]);
    // Teilnehmer-Auswahl (für Sammel-Mail)
    const [selectedBookingIds, setSelectedBookingIds] = useState([]);
    const [mailComposerOpen, setMailComposerOpen] = useState(false);
    const [participantExportOpen, setParticipantExportOpen] = useState(false);
    // Persisted per-seminar participant filter – überlebt Navigation zu Teilnehmer-Detail und zurück.
    const participantFilterKey = `bgpr_participant_filter_${id}`;
    const [participantFilter, setParticipantFilter] = useState(() => {
        if (isNew) return 'all';
        try { return sessionStorage.getItem(participantFilterKey) || 'all'; } catch { return 'all'; }
    });
    useEffect(() => {
        if (isNew) return;
        try { sessionStorage.setItem(participantFilterKey, participantFilter); } catch { /* */ }
    }, [participantFilter, participantFilterKey, isNew]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [priceEditBooking, setPriceEditBooking] = useState(null); // {booking_id, name, final_price}
    const [priceInput, setPriceInput] = useState('');
    const [savingPrice, setSavingPrice] = useState(false);
    const [seminarTitle, setSeminarTitle] = useState('');
    const [currentParticipants, setCurrentParticipants] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [referentsMap, setReferentsMap] = useState({});

    useEffect(() => {
        referentApi.list().then(r => {
            const map = {};
            (r.data || []).forEach(x => { map[x.id] = x; });
            setReferentsMap(map);
        }).catch(() => {});
    }, []);

    const currentReferentDisplay = (() => {
        const ids = formData.referent_ids || [];
        if (!ids.length) return formData.instructor_name || '—';
        return ids.map(i => referentsMap[i]?.display_name || '').filter(Boolean).join(', ') || '—';
    })();

    useEffect(() => {
        templateApi.getAll().then(r => setTemplates(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        settingsApi.getLicenseTypes()
            .then(r => setLicenseTypes(r.data.items || []))
            .catch(() => setLicenseTypes([]));
    }, []);

    const getYearFromDate = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr.includes('-')) return parseInt(dateStr.split('-')[0]);
        return null;
    };

    const fetchNextNumber = useCallback(async (year) => {
        try { const res = await seminarApi.nextNumber(year); return res.data.course_number; } catch { return ''; }
    }, []);

    useEffect(() => {
        if (isNew) { fetchNextNumber().then(num => setFormData(prev => ({ ...prev, course_number: num }))); }
        else { loadSeminar(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!isNew && activeTab === 'participants') loadParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const loadSeminar = async () => {
        try {
            const s = (await seminarApi.getOne(id)).data;
            setSeminarTitle(s.title);
            setCurrentParticipants(s.current_participants || 0);
            setCourseConfirmed(s.course_confirmed || false);
            setIsCancelled(!!s.is_cancelled);
            setCancellationReason(s.cancellation_reason || '');
            setFormData({
                title: s.title || '', subtitle: s.subtitle || '',
                course_number: s.course_number || '', description: s.description || '',
                dates: s.dates?.length ? s.dates.map(d => ({ ...emptyDate, ...d })) : [{ ...emptyDate }],
                date: s.date || '', time_start: s.time_start || '09:00', time_end: s.time_end || '17:00',
                date_mode: s.date_mode || 'individual',
                date_range_start: s.date_range_start || '',
                date_range_end: s.date_range_end || '',
                location: s.location || '', instructor_name: s.instructor_name || '',
                referent_ids: s.referent_ids || [],
                ue_count: s.ue_count || 8, max_participants: s.max_participants || 20,
                min_participants: s.min_participants || 5, price: s.price || 0,
                discounts: s.discounts?.length ? s.discounts.map(d => ({ ...emptyDiscount, ...d })) : [],
                external_link: s.external_link || '', organizer: s.organizer || 'BGPR-Akademie',
                organizer_type: s.organizer_type || 'bgpr', organizer_name: s.organizer_name || '',
                event_category: s.event_category || 'fortbildung', event_format: s.event_format || 'praesenz',
                training_type: s.training_type || '', training_license_type_id: s.training_license_type_id || null, requires_prerequisites: s.requires_prerequisites || false,
                certificate_type: s.certificate_type || 'keine',
                certificate_content: s.certificate_content || '', is_published: s.is_published || false,
                is_first_aid: s.is_first_aid || false,
                survey_link: s.survey_link || '',
                registration_deadline_days: s.registration_deadline_days ?? '',
                registration_deadline_effective: s.registration_deadline_effective || '',
                is_in_house: !!s.is_in_house,
                in_house_company_name: s.in_house_company?.name || '',
                in_house_contact_person: s.in_house_company?.contact_person || '',
                in_house_email: s.in_house_company?.email || '',
                in_house_phone: s.in_house_company?.phone || '',
                in_house_price_type: s.in_house_price_type || 'flat',
                in_house_flat_price: s.in_house_flat_price ?? '',
                in_house_per_participant_price: s.in_house_per_participant_price ?? '',
                in_house_max_participants: s.in_house_max_participants ?? '',
                in_house_signup_token: s.in_house_signup_token || '',
            });
            originalIsFirstAidRef.current = !!s.is_first_aid;
        } catch { toast.error('Seminar nicht gefunden'); navigate('/admin/seminars'); }
        finally { setLoading(false); }
    };

    const loadParticipants = async () => {
        setLoadingParticipants(true);
        try { const data = (await seminarApi.getParticipants(id)).data; setParticipants(data); setCurrentParticipants(data.filter(p => p.status !== 'cancelled').length); }
        catch { toast.error('Fehler'); }
        finally { setLoadingParticipants(false); }
    };

    // ===== Warteliste =====
    const [waitingList, setWaitingList] = useState([]);
    const [loadingWaitingList, setLoadingWaitingList] = useState(false);
    const loadWaitingList = useCallback(async () => {
        setLoadingWaitingList(true);
        try {
            const data = (await bookingApi.getSeminarWaitingList(id)).data;
            setWaitingList(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Warteliste konnte nicht geladen werden');
        } finally {
            setLoadingWaitingList(false);
        }
    }, [id]);
    useEffect(() => {
        if (!isNew && activeTab === 'waiting-list') loadWaitingList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, isNew]);

    // Beim Seminar-Load auch einmal die Warteliste-Länge holen, damit der Tab-Counter korrekt ist.
    useEffect(() => {
        if (!isNew) loadWaitingList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handlePromote = async (bookingId) => {
        try {
            await bookingApi.promoteWaitingList(bookingId);
            toast.success('Annehmen-/Ablehnen-Mail wurde versendet (48 h Frist)');
            await loadWaitingList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Promotion fehlgeschlagen');
        }
    };
    const handleRemoveFromWaitingList = async (bookingId) => {
        if (!window.confirm('Diesen Wartelisten-Eintrag wirklich entfernen?')) return;
        try {
            await bookingApi.cancelSelf(bookingId);
            toast.success('Eintrag entfernt');
            await loadWaitingList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Entfernen fehlgeschlagen');
        }
    };

    // Detail-Drawer für Warteliste-Eintrag (Mail-Historie + Buchungs-Link)
    const [waitingDetailId, setWaitingDetailId] = useState(null);

    const handleChange = (e) => {
        let value;
        if (e.target.type === 'number') {
            // Leere Eingabe als '' belassen (für optionale Felder wie registration_deadline_days)
            value = e.target.value === '' ? '' : (parseFloat(e.target.value) || 0);
        } else {
            value = e.target.value;
        }
        setFormData({ ...formData, [e.target.name]: value });
    };
    const addDate = () => setFormData({ ...formData, dates: [...formData.dates, { ...emptyDate }] });
    const removeDate = (idx) => { if (formData.dates.length <= 1) return; setFormData({ ...formData, dates: formData.dates.filter((_, i) => i !== idx) }); };
    const updateDate = async (idx, field, value) => {
        const updated = formData.dates.map((d, i) => i === idx ? { ...d, [field]: value } : d);
        setFormData(prev => ({ ...prev, dates: updated }));
        if (isNew && idx === 0 && field === 'date' && value && formData.organizer_type === 'bgpr') {
            const year = getYearFromDate(value);
            if (year) { const n = await fetchNextNumber(year); setFormData(prev => ({ ...prev, course_number: n })); }
        }
    };
    const addDiscount = () => setFormData({ ...formData, discounts: [...formData.discounts, { ...emptyDiscount }] });
    const removeDiscount = (idx) => setFormData({ ...formData, discounts: formData.discounts.filter((_, i) => i !== idx) });
    const updateDiscount = (idx, field, value) => {
        setFormData({ ...formData, discounts: formData.discounts.map((d, i) => i === idx ? { ...d, [field]: value } : d) });
    };

    const handleSave = async () => {
        if (!formData.title || !formData.location) { toast.error('Bitte Titel und Ort ausfüllen'); return; }
        if (formData.date_mode === 'range') {
            if (!formData.date_range_start || !formData.date_range_end) {
                toast.error('Bitte Beginn- und End-Datum des Zeitraums angeben');
                return;
            }
            if (formData.date_range_end < formData.date_range_start) {
                toast.error('End-Datum muss nach dem Beginn-Datum liegen');
                return;
            }
        }
        // Plausibilitätsprüfung: Firmenfortbildung (In-House) → Firmenname pflicht
        if (formData.is_in_house) {
            if (!formData.in_house_company_name || formData.in_house_company_name.trim().length < 2) {
                toast.error('Bitte Firmenname für die In-House-Fortbildung angeben');
                return;
            }
            const pt = formData.in_house_price_type;
            const flat = parseFloat(formData.in_house_flat_price);
            const per = parseFloat(formData.in_house_per_participant_price);
            if (pt === 'flat' && (!isFinite(flat) || flat < 0)) {
                toast.error('Bitte einen gültigen Pauschalpreis für die Firmenfortbildung angeben');
                return;
            }
            if (pt === 'per_participant' && (!isFinite(per) || per < 0)) {
                toast.error('Bitte einen gültigen Preis pro Teilnehmer angeben');
                return;
            }
        }
        setSaving(true);
        try {
            const payload = { ...formData };
            // Computed read-only Felder NICHT senden (Backend würde sie evtl. ablehnen)
            delete payload.registration_deadline_effective;
            delete payload.registration_closed;
            // In-House-Formularfelder → strukturiertes Payload für Backend zusammenbauen
            if (payload.is_in_house) {
                payload.in_house_company = {
                    name: (payload.in_house_company_name || '').trim(),
                    contact_person: (payload.in_house_contact_person || '').trim() || null,
                    email: (payload.in_house_email || '').trim() || null,
                    phone: (payload.in_house_phone || '').trim() || null,
                };
                const flat = parseFloat(payload.in_house_flat_price);
                const per = parseFloat(payload.in_house_per_participant_price);
                const maxp = parseInt(payload.in_house_max_participants, 10);
                payload.in_house_flat_price = payload.in_house_price_type === 'flat' && isFinite(flat) ? flat : null;
                payload.in_house_per_participant_price = payload.in_house_price_type === 'per_participant' && isFinite(per) ? per : null;
                payload.in_house_max_participants = Number.isFinite(maxp) && maxp > 0 ? maxp : null;
                payload.is_published = false; // Sicherheit — In-House nie öffentlich
            } else {
                // Wenn nicht In-House, Felder auf null zurücksetzen (Historie bleibt aber gemäss Backend erhalten)
                payload.in_house_company = null;
                payload.in_house_price_type = null;
                payload.in_house_flat_price = null;
                payload.in_house_per_participant_price = null;
                payload.in_house_max_participants = null;
            }
            // Hilfsfelder nicht ans Backend senden
            delete payload.in_house_company_name;
            delete payload.in_house_contact_person;
            delete payload.in_house_email;
            delete payload.in_house_phone;
            delete payload.in_house_signup_token;
            // registration_deadline_days: leere Strings -> null umwandeln
            if (payload.registration_deadline_days === '' || payload.registration_deadline_days === undefined) {
                payload.registration_deadline_days = null;
            } else {
                payload.registration_deadline_days = parseInt(payload.registration_deadline_days, 10);
                if (Number.isNaN(payload.registration_deadline_days) || payload.registration_deadline_days < 0) {
                    payload.registration_deadline_days = null;
                }
            }
            // Im Zeitraum-Modus überschreiben wir die Termin-Liste mit einem
            // synthetischen Eintrag, damit bestehende Listen, Mails und PDFs einen
            // sinnvollen Anker haben (= das Beginn-Datum). Format/Zeiten bleiben leer
            // bzw. übernehmen das Standard-Event-Format.
            if (payload.date_mode === 'range') {
                const fmt = payload.event_format || 'praesenz';
                payload.dates = [{ date: payload.date_range_start, time_start: '', time_end: '', format: fmt, meeting_link: '' }];
                payload.date = payload.date_range_start;
                payload.time_start = '';
                payload.time_end = '';
            } else {
                payload.date_range_start = null;
                payload.date_range_end = null;
                if (payload.dates?.length > 0) {
                    payload.date = payload.dates[0].date;
                    payload.time_start = payload.dates[0].time_start;
                    payload.time_end = payload.dates[0].time_end;
                }
            }
            if (isNew) {
                const res = await seminarApi.create(payload);
                toast.success('Fortbildung erstellt');
                navigate(`/admin/seminars/${res.data.id}`, { replace: true });
            } else {
                await seminarApi.update(id, payload);
                setSeminarTitle(formData.title);
                setEditMode(false);
                toast.success('Gespeichert');
                // Plausibilitäts-Hinweis: Wenn der Erste-Hilfe-Schalter gerade gekippt
                // wurde UND bereits Teilnehmer anwesend waren, hängen die alten
                // training_records noch am alten record_type. Wir bieten dem Admin
                // sofort die Reparatur an, damit das nicht still verloren geht.
                const prev = originalIsFirstAidRef.current;
                const next = !!payload.is_first_aid;
                const hadAttended = participants.some(p => p.attended);
                if (prev !== null && prev !== next && hadAttended) {
                    const targetLabel = next ? 'Erste-Hilfe-Nachweis' : 'reguläre Fortbildung';
                    if (window.confirm(
                        `Hinweis: „Erste-Hilfe-Kurs" wurde von ${prev ? 'AN' : 'AUS'} auf ${next ? 'AN' : 'AUS'} umgeschaltet.\n\n`
                        + `Bereits ausgestellte Lehrgangsbescheinigungen sind noch mit dem alten Verbuchungs-Typ gespeichert.\n\n`
                        + `Sollen diese Nachweise jetzt auf „${targetLabel}" angepasst werden?\n`
                        + `(Bereits für eine Lizenz-Verlängerung verbrauchte Nachweise bleiben unberührt.)`
                    )) {
                        await handleResyncTrainingRecords({ skipConfirm: true });
                    }
                }
                originalIsFirstAidRef.current = next;
            }
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
        finally { setSaving(false); }
    };

    const [deleteImpact, setDeleteImpact] = useState(null);
    useEffect(() => {
        if (!deleteDialogOpen) { setDeleteImpact(null); return; }
        seminarApi.getDeleteImpact(id).then(r => setDeleteImpact(r.data)).catch(() => setDeleteImpact(null));
    }, [deleteDialogOpen, id]);

    const handleDelete = async () => {
        try { await seminarApi.delete(id); toast.success('Gelöscht'); navigate('/admin/seminars'); }
        catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const handleTogglePublish = async () => {
        try {
            const res = await seminarApi.togglePublish(id);
            setFormData(prev => ({ ...prev, is_published: res.data.is_published }));
            toast.success(res.data.is_published ? 'Online' : 'Offline');
        } catch { toast.error('Fehler'); }
    };

    // === Participant handlers ===
    const seminarDates = formData.dates?.filter(d => d.date).map(d => d.date) || [];
    const hasOnlineOrHybrid = (formData.dates || []).some(d => d.format === 'online' || d.format === 'hybrid');

    const handleToggleAttendance = async (bookingId, date, current) => {
        try {
            await bookingApi.markAttendance(bookingId, !current, date);
            loadParticipants();
        } catch { toast.error('Fehler'); }
    };

    const handleCreateInvoice = async (bookingId) => {
        try { await invoiceApi.create({ booking_id: bookingId, amount: formData.price }); toast.success('Rechnung erstellt'); loadParticipants(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };
    const handleBatchInvoices = async () => {
        try { const r = await seminarApi.batchInvoices(id); toast.success(r.data.message); loadParticipants(); } catch { toast.error('Fehler'); }
    };
    const handleMarkAllAttended = async () => {
        const unattended = participants.filter(p => !p.attended && p.status !== 'cancelled');
        for (const p of unattended) await bookingApi.markAttendance(p.booking_id, true);
        toast.success(`${unattended.length} Teilnehmer als anwesend markiert`);
        loadParticipants();
    };
    const handleDownloadParticipantList = () => setParticipantExportOpen(true);
    const handleDownloadAttendanceList = async () => {
        try { const r = await seminarApi.attendanceListPdf(id); downloadBlob(new Blob([r.data]), `Anwesenheitsliste_${formData.course_number}.pdf`); }
        catch { toast.error('Fehler'); }
    };

    const handleDownloadBulkCertificates = async () => {
        try {
            toast.info('Bescheinigungen werden gesammelt erzeugt...');
            const r = await seminarApi.bulkCertificatesPdf(id);
            downloadBlob(new Blob([r.data], { type: 'application/pdf' }), `Bescheinigungen_${formData.course_number}.pdf`);
            toast.success('Sammel-PDF heruntergeladen');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Erzeugen der Sammel-PDF');
        }
    };

    // Sammel-Versand: Bescheinigungen per Mail an ausgewählte Teilnehmer.
    // Hängt an der existierenden Checkbox-Auswahl (`visibleSelected`) und nutzt
    // den Backend-Endpoint /api/pdf/certificate/bulk-send-email aus Iter67.
    const [bulkCertMailRunning, setBulkCertMailRunning] = useState(false);
    const [bulkRegenRunning, setBulkRegenRunning] = useState(false);
    const [previewCertId, setPreviewCertId] = useState(null);

    // Frische PDF für einen einzelnen Teilnehmer direkt in den Browser laden —
    // ohne Mail, ohne Umweg über Dropbox. Ideal zur Adobe-Kompat-Prüfung.
    // Cache-Buster (`?v=timestamp`) verhindert, dass der Browser eine alte
    // (evtl. kaputte) Version aus dem HTTP-Cache serviert.
    const handlePreviewCert = async (p) => {
        setPreviewCertId(p.booking_id);
        try {
            const r = await bookingApi.downloadCertificate(p.booking_id, { cacheBuster: Date.now() });
            // participant_name ist der einzige zuverlässige Namens-Feld in der Liste
            const rawName = (p.participant_name || 'Teilnehmer').trim();
            const cleanName = rawName.replace(/[^\w -]/g, '').replace(/\s+/g, '_') || 'Teilnehmer';
            downloadBlob(r.data, `${cleanName}_Bescheinigung.pdf`);
            toast.success('Frische PDF geladen (Cache umgangen)');
        } catch (e) {
            const raw = e.response?.data;
            let msg = 'Download fehlgeschlagen';
            if (raw instanceof Blob) {
                try { msg = JSON.parse(await raw.text()).detail || msg; } catch { /* ignore */ }
            } else if (raw?.detail) msg = raw.detail;
            toast.error(msg);
        } finally {
            setPreviewCertId(null);
        }
    };

    const handleBulkRegenerateCerts = async () => {
        const eligible = participants
            .filter(p => visibleSelected.includes(p.booking_id) && p.attended && p.status !== 'cancelled')
            .map(p => p.booking_id);
        const skipped = visibleSelected.length - eligible.length;
        if (eligible.length === 0) {
            toast.error('Keine geeigneten Buchungen ausgewählt (Teilnehmer müssen anwesend gewesen sein).');
            return;
        }
        const skippedLine = skipped > 0 ? `\n\n⚠ ${skipped} Auswahl(en) übersprungen (nicht anwesend).` : '';
        const msg = `${eligible.length} Bescheinigung(en) NEU generieren, ohne E-Mail-Versand?\n\n`
            + `Das ist der „Prüf-Modus":\n`
            + `• PDFs werden mit dem aktuellen Engine-Code neu erzeugt\n`
            + `• Portal-Downloads + Dropbox-Ordner werden aktualisiert\n`
            + `• Es gehen KEINE E-Mails an die Teilnehmer raus\n\n`
            + `Nutzen Sie das nach einem Fix, um die neuen PDFs selbst zu prüfen, bevor Sie sie ausrollen.${skippedLine}`;
        if (!window.confirm(msg)) return;
        setBulkRegenRunning(true);
        try {
            toast.info(`Regeneriere ${eligible.length} Bescheinigung(en)…`);
            const r = await bookingApi.bulkRegenerateCertificates(eligible);
            const d = r.data || {};
            const results = d.results || [];
            // Dropbox-Status aggregieren, damit User sofort sieht was schief lief
            const dbxUploaded = results.filter(x => x.dropbox?.status === 'uploaded').length;
            const dbxSkipped = results.filter(x => x.dropbox?.status === 'skipped').length;
            const dbxErrors = results.filter(x => x.dropbox?.status === 'error');
            let dbxSummary = '';
            if (results.length > 0) {
                if (dbxUploaded === results.length) {
                    dbxSummary = ` · ✅ ${dbxUploaded} in Dropbox archiviert`;
                } else {
                    dbxSummary = ` · Dropbox: ${dbxUploaded} OK, ${dbxSkipped} übersprungen, ${dbxErrors.length} Fehler`;
                }
            }
            toast.success((d.message || 'Regeneration abgeschlossen') + dbxSummary, { duration: 6000 });
            // Bei Dropbox-Problemen konkret zeigen was los ist
            if (dbxErrors.length > 0 || dbxSkipped > 0) {
                const first = dbxErrors[0]?.dropbox?.reason || results.find(x => x.dropbox?.status === 'skipped')?.dropbox?.reason;
                if (first) toast.warning(`Dropbox-Grund: ${first}`, { duration: 10000 });
            }
            if (d.failed > 0) {
                const errs = results.filter(x => x.status === 'error').slice(0, 3)
                    .map(x => `• ${x.booking_id.slice(0, 8)}: ${x.reason}`).join('\n');
                if (errs) toast.error(`${d.failed} Fehler:\n${errs}`);
            }
            loadParticipants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Regeneration fehlgeschlagen');
        } finally {
            setBulkRegenRunning(false);
        }
    };

    const handleBulkSendCertEmails = async () => {
        const eligible = participants
            .filter(p => visibleSelected.includes(p.booking_id) && p.attended && p.status !== 'cancelled')
            .map(p => p.booking_id);
        const skipped = visibleSelected.length - eligible.length;
        if (eligible.length === 0) {
            toast.error('Keine geeigneten Buchungen ausgewählt (Teilnehmer müssen anwesend gewesen sein).');
            return;
        }
        // Plausibilitäts-Hinweis: Verbuchungs-Typ ist beim Bescheinigungs-Versand
        // entscheidend (regular vs. first_aid) — Admin soll das vor dem Massenversand
        // bewusst absegnen, damit später keine Records mit falschem record_type entstehen.
        const isFA = !!formData.is_first_aid;
        const ueCount = Number(formData.ue_count || 0);
        const bookingLine = isFA
            ? '🫀 ERSTE-HILFE-NACHWEIS  (für 1.-Hilfe-Pflicht der Lizenz-Verlängerung)'
            : `📝 REGULÄRE FORTBILDUNG${ueCount > 0 ? ` (${ueCount} UE auf das Mitglied-Konto)` : ''}`;
        const skippedLine = skipped > 0 ? `\n\n⚠ ${skipped} Auswahl(en) übersprungen (nicht anwesend).` : '';
        const msg = `An ${eligible.length} Teilnehmer Bescheinigung per Mail senden?\n\n`
            + `Verbuchungs-Typ laut Seminar-Einstellung:\n${bookingLine}\n\n`
            + `Falls falsch → erst „Erste-Hilfe-Kurs"-Schalter im Seminar prüfen.${skippedLine}`;
        if (!window.confirm(msg)) return;
        setBulkCertMailRunning(true);
        try {
            toast.info(`Versende ${eligible.length} Bescheinigungen…`);
            const r = await bookingApi.bulkSendCertificatesByEmail(eligible);
            const d = r.data || {};
            toast.success(d.message || 'Versand abgeschlossen');
            if (d.failed > 0) {
                const errs = (d.results || []).filter(x => x.status === 'error').slice(0, 3)
                    .map(x => `• ${x.booking_id.slice(0, 8)}: ${x.reason}`).join('\n');
                if (errs) toast.error(`${d.failed} Versand(e) fehlgeschlagen:\n${errs}`);
            }
            loadParticipants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Sammel-Versand fehlgeschlagen');
        } finally {
            setBulkCertMailRunning(false);
        }
    };

    const handleConfirmCourse = async () => {
        try {
            const res = await seminarApi.confirmCourse(id);
            setCourseConfirmed(true);
            toast.success(res.data.message);
            loadParticipants();
        } catch { toast.error('Fehler bei der Kursbestätigung'); }
    };

    // Backfill: Erstellt rückwirkend offene Rechnungen für confirmed Buchungen
    // ohne Rechnung. Notwendig für Seminare, die VOR dem 05/2026-Fix
    // bestätigt wurden (damals legte confirm_course keine Rechnungen an, so
    // dass die „Erwartet (Seminargebühren)"-Summe in der Buchhaltung 0 zeigte).
    const [backfillRunning, setBackfillRunning] = useState(false);
    const handleBackfillInvoices = async () => {
        if (!window.confirm('Fehlende offene Forderungen für alle bestätigten, kostenpflichtigen Teilnehmer dieses Seminars rückwirkend erfassen? Dies legt nur interne Buchführungs-Notizen an — es wird keine Rechnung erstellt oder versandt. (Idempotent — vorhandene Forderungen bleiben unverändert.)')) return;
        setBackfillRunning(true);
        try {
            const res = await seminarApi.backfillInvoices(id);
            const d = res.data || {};
            if (d.created > 0) {
                toast.success(d.message || `${d.created} Forderung(en) erfasst`);
            } else {
                toast.info(d.message || 'Es waren bereits alle Forderungen erfasst — nichts zu tun.');
            }
            loadParticipants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reparatur fehlgeschlagen');
        } finally {
            setBackfillRunning(false);
        }
    };

    // Heile training_records: setzt record_type aller auto-erstellten
    // Nachweise an `seminar.is_first_aid` an — Reparatur, wenn ein Workshop
    // nachträglich als Erste-Hilfe-Kurs markiert wurde oder ein Auto-Credit
    // den Wert falsch übernommen hat.
    const [recordResyncRunning, setRecordResyncRunning] = useState(false);
    const handleResyncTrainingRecords = async (opts = {}) => {
        if (!opts.skipConfirm && !window.confirm('Den record_type aller automatisch erstellten Fortbildungsnachweise dieses Seminars an die aktuelle „Erste-Hilfe-Kurs"-Einstellung anpassen? Bereits für eine Lizenz-Verlängerung verbrauchte Nachweise bleiben unberührt.')) return;
        setRecordResyncRunning(true);
        try {
            const res = await seminarApi.resyncTrainingRecords(id);
            const d = res.data || {};
            const skipped = (d.skipped_used_for_renewal || []).length;
            if (d.updated > 0) {
                toast.success(`${d.updated} Nachweis(e) auf „${d.target_record_type === 'first_aid' ? 'Erste-Hilfe' : 'Regulär'}" angepasst${skipped ? ` · ${skipped} bereits für Verlängerung verbraucht – übersprungen` : ''}`);
            } else {
                toast.info(`Bereits alle ${d.already_correct} Nachweis(e) korrekt auf „${d.target_record_type === 'first_aid' ? 'Erste-Hilfe' : 'Regulär'}" – nichts zu tun.${skipped ? ` ${skipped} für Verlängerung verbraucht (unberührt).` : ''}`);
            }
            loadParticipants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reparatur fehlgeschlagen');
        } finally {
            setRecordResyncRunning(false);
        }
    };

    const openPriceEdit = (participant) => {
        setPriceEditBooking({
            booking_id: participant.booking_id,
            name: participant.participant_name || participant.participant_email || 'Teilnehmer',
            old_price: participant.final_price ?? 0,
            payment_status: participant.payment_status,
        });
        setPriceInput(String(participant.final_price ?? 0));
    };

    const handleSavePrice = async () => {
        if (!priceEditBooking) return;
        const normalized = priceInput.replace(',', '.').trim();
        const parsed = parseFloat(normalized);
        if (Number.isNaN(parsed) || parsed < 0) {
            toast.error('Bitte einen gültigen Preis eingeben (z. B. 49,50)');
            return;
        }
        setSavingPrice(true);
        try {
            const res = await bookingApi.updatePrice(priceEditBooking.booking_id, parsed);
            if (res.data?.invoice_updated) {
                toast.success(`Preis aktualisiert (offene Rechnung mitgezogen: ${parsed.toFixed(2)} €)`);
            } else {
                toast.success(`Preis aktualisiert: ${parsed.toFixed(2)} €`);
            }
            setPriceEditBooking(null);
            setPriceInput('');
            loadParticipants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Preisänderung fehlgeschlagen');
        } finally {
            setSavingPrice(false);
        }
    };

    if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></AdminLayout>;

    const confirmedCount = participants.filter(p => p.status !== 'cancelled').length;
    const attendedCount = participants.filter(p => p.attended).length;
    const paidCount = participants.filter(p => p.payment_status === 'paid').length;
    const minReached = confirmedCount >= formData.min_participants;

    // Filter-Logik für Teilnehmerliste (wird für Tabelle + Blätterfunktion genutzt).
    const matchesFilter = (p) => {
        switch (participantFilter) {
            case 'all': return true;
            case 'paid': return !p.is_referent && p.payment_status === 'paid';
            case 'partial': return !p.is_referent && p.payment_status === 'partial';
            case 'open': return !p.is_referent && !p.is_free && p.status !== 'cancelled' && p.payment_status !== 'paid' && p.payment_status !== 'partial';
            case 'cancelled': return p.status === 'cancelled';
            case 'referent': return !!p.is_referent;
            default: return true;
        }
    };
    const filteredParticipants = participants.filter(matchesFilter);
    const filteredBookingIds = filteredParticipants.map(p => p.booking_id);
    // Stornierte Teilnehmer sind weder für „Alle auswählen" noch per Einzel-
    // Checkbox auswählbar. Sie haben weder Anwesenheit noch Zahlung —
    // Bescheinigungs- oder Mail-Versand an sie ergibt keinen Sinn.
    const selectableBookingIds = filteredParticipants
        .filter(p => p.status !== 'cancelled')
        .map(p => p.booking_id);

    // Auswahl nur auf sichtbare (gefilterte) Teilnehmer beschränken.
    const visibleSelected = selectedBookingIds.filter(id => filteredBookingIds.includes(id));
    const allFilteredSelected = selectableBookingIds.length > 0
        && selectableBookingIds.every(id => selectedBookingIds.includes(id));
    const toggleSelectAllVisible = () => {
        setSelectedBookingIds(prev => {
            // Stornierte auf jeden Fall aus der Auswahl entfernen.
            const cleared = prev.filter(id =>
                !filteredBookingIds.includes(id)
                || selectableBookingIds.includes(id),
            );
            if (allFilteredSelected) {
                // Aktuell alle ausgewählt → komplett abwählen (auch eventuell
                // dazwischen gemogelte cancelled).
                return cleared.filter(id => !selectableBookingIds.includes(id));
            }
            // Sonst: alle auswählbaren hinzufügen.
            const merged = new Set([...cleared, ...selectableBookingIds]);
            return Array.from(merged);
        });
    };
    const toggleRowSelected = (bookingId) => {
        setSelectedBookingIds(prev => prev.includes(bookingId)
            ? prev.filter(x => x !== bookingId)
            : [...prev, bookingId]);
    };
    const selectedRecipients = participants
        .filter(p => selectedBookingIds.includes(p.booking_id))
        .map(p => ({ booking_id: p.booking_id, participant_name: p.participant_name, participant_email: p.participant_email }));

    const FILTER_LABELS = {
        all: 'Alle Teilnehmer',
        paid: 'Bezahlt',
        partial: 'Teilbezahlt',
        open: 'Offener Betrag',
        cancelled: 'Storniert',
        referent: 'Referent',
    };

    const openParticipant = (bId) => {
        navigate(`/admin/seminars/${id}/participants/${bId}`, {
            state: {
                bookingIds: filteredBookingIds,
                filterLabel: FILTER_LABELS[participantFilter] || 'Teilnehmer',
                seminarId: id,
            },
        });
    };

    const isExternal = formData.organizer_type === 'external';

    // Resolve certificate_type label from templates (or fallback to legacy CERT_TYPES)
    const certTypeLabel = (() => {
        const v = formData.certificate_type;
        if (!v || v === 'keine') return 'Keine Bescheinigung';
        const t = templates.find(x => x.id === v);
        if (t) return t.name;
        return CERT_TYPES[v] || v;
    })();

    // ===== RENDER =====
    return (
        <AdminLayout>
            {/* Color accent bar */}
            {!isNew && (
                <div className={`h-1.5 -mx-8 -mt-6 mb-6 rounded-b ${isExternal ? 'bg-[#E5007D]' : 'bg-primary'}`} />
            )}

            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/seminars')} data-testid="back-to-seminars">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
                </Button>
                <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold truncate" data-testid="seminar-page-title">
                        {isNew ? 'Neue Fortbildung' : (seminarTitle || 'Fortbildung')}
                    </h1>
                    {!isNew && formData.course_number && <p className="text-sm text-muted-foreground font-mono">{formData.course_number}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {!isNew && !editMode && activeTab === 'details' && (
                        <>
                            <Button variant="outline" size="sm" onClick={handleTogglePublish} data-testid="toggle-publish">
                                {formData.is_published ? <><EyeOff className="h-4 w-4 mr-1" /> Offline schalten</> : <><Eye className="h-4 w-4 mr-1" /> Online schalten</>}
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-seminar">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                    setDuplicating(true);
                                    try {
                                        const r = await seminarApi.duplicate(id);
                                        toast.success('Kurs dupliziert');
                                        // Vollständiger Reload, damit die neue Detailseite die frischen Daten laedt
                                        // (state der aktuellen Komponente wuerde sonst noch beim alten Seminar bleiben)
                                        window.location.assign(`/admin/seminars/${r.data.id}`);
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || 'Duplizieren fehlgeschlagen');
                                    } finally {
                                        setDuplicating(false);
                                    }
                                }}
                                disabled={duplicating}
                                data-testid="duplicate-seminar"
                                title="Kurs duplizieren"
                            >
                                {duplicating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Copy className="h-4 w-4 mr-1" />Duplizieren</>}
                            </Button>
                            {formData?.is_in_house && (
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => setInHouseDialogOpen(true)}
                                    data-testid="in-house-btn"
                                    title="In-House verwalten – Link kopieren, Sammelrechnung"
                                    className="bg-indigo-600 hover:bg-indigo-700"
                                >
                                    <Building2 className="h-4 w-4 mr-1" />
                                    In-House verwalten
                                </Button>
                            )}
                            <Button onClick={() => setEditMode(true)} data-testid="edit-seminar">
                                <Pencil className="h-4 w-4 mr-1" /> Bearbeiten
                            </Button>
                        </>
                    )}
                    {(isNew || editMode) && (
                        <>
                            {!isNew && <Button variant="outline" size="sm" onClick={() => { setEditMode(false); loadSeminar(); }}>Abbrechen</Button>}
                            <Button onClick={handleSave} disabled={saving} data-testid="save-seminar">
                                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                                Speichern
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Tabs */}
            {!isNew && (
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 mb-6 w-fit">
                    {[
                        { value: 'details', label: 'Seminardaten' },
                        { value: 'participants', label: `Teilnehmer (${currentParticipants})` },
                        { value: 'waiting-list', label: `Warteliste${waitingList.length ? ` (${waitingList.length})` : ''}` },
                    ].map((t) => (
                        <button key={t.value} onClick={() => handleTabChange(t.value)}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === t.value ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid={`tab-${t.value}`}>{t.label}</button>
                    ))}
                </div>
            )}

            {/* ========= STAMMDATEN ========= */}
            {(isNew || activeTab === 'details') && !editMode && !isNew && (
                <div className="max-w-4xl space-y-6">
                    <Card><CardContent className="pt-6 space-y-4">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                            <InfoRow label="Titel" value={formData.title} />
                            <InfoRow label="Kursnummer" value={formData.course_number} mono />
                            <InfoRow label="Untertitel" value={formData.subtitle} />
                            <InfoRow label="Veranstalter" value={formData.organizer_type === 'bgpr' ? (formData.is_in_house ? 'BGPR-Akademie · In-House' : 'BGPR-Akademie') : (formData.organizer_name || 'Extern')} className={formData.is_in_house ? 'text-indigo-700 font-medium' : ''} />
                            {formData.organizer_type === 'bgpr' && <>
                                <InfoRow label="Veranstaltungsart" value={CATEGORY[formData.event_category] || formData.event_category} />
                                <InfoRow label="Format" value={FORMAT[formData.event_format] || formData.event_format} />
                                {formData.event_category === 'ausbildung' && formData.training_type && (
                                    <InfoRow label="Ausbildungstyp" value={TRAINING_TYPE[formData.training_type] || formData.training_type} />
                                )}
                                {formData.requires_prerequisites && (
                                    <InfoRow label="Vorkenntnisse" value="Erforderlich (verkürzte Ausbildung)" className="text-amber-600 font-medium" />
                                )}
                            </>}
                            <InfoRow label="Ort" value={formData.location} />
                            <InfoRow label="Referenten" value={currentReferentDisplay} />
                            <InfoRow label="UE" value={`${formData.ue_count} Unterrichtseinheiten`} />
                            <InfoRow label="Seminargebühr" value={formatCurrency(formData.price)} />
                            <InfoRow label="Teilnehmer" value={`min. ${formData.min_participants} / max. ${formData.max_participants}`} />
                            <InfoRow label="Status" value={formData.is_published ? 'Online' : 'Offline'} className={formData.is_published ? 'text-green-600 font-medium' : 'text-gray-400'} />
                            <InfoRow
                                label="Anmeldeschluss"
                                value={
                                    formData.registration_deadline_days !== '' && formData.registration_deadline_days !== null && formData.registration_deadline_days !== undefined
                                        ? `${formData.registration_deadline_days} Tag${Number(formData.registration_deadline_days) === 1 ? '' : 'e'} vor Seminarbeginn${formData.registration_deadline_effective ? ` (= ${new Date(`${formData.registration_deadline_effective}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })})` : ''}`
                                        : '— (bis Seminartag)'
                                }
                                className={(formData.registration_deadline_days !== '' && formData.registration_deadline_days !== null && formData.registration_deadline_days !== undefined) ? '' : 'text-gray-400'}
                            />
                        </div>
                        {formData.discounts?.length > 0 && (
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Rabatte</p>
                                <div className="space-y-1.5">
                                    {formData.discounts.map((d, i) => (
                                        <div key={i} className="flex items-center justify-between p-2 bg-emerald-50/50 rounded border border-emerald-200/50 text-sm">
                                            <div className="flex-1">
                                                <span className="font-medium">{d.name}</span>
                                                {d.description && <span className="text-xs text-muted-foreground ml-2">· {d.description}</span>}
                                                {d.requires_verein_name && <span className="text-xs text-emerald-700 ml-2">· Vereinsname-Abfrage</span>}
                                            </div>
                                            <span className="font-bold text-emerald-700">−{d.discount_value}{d.discount_type === 'percent' ? '%' : ' €'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {formData.date_mode === 'range' && formData.date_range_start ? (
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Zeitraum</p>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-lg text-sm">
                                    {fmtDate(formData.date_range_start)} – {fmtDate(formData.date_range_end)}
                                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Zeitraum</span>
                                </span>
                                <p className="text-xs text-muted-foreground mt-1">Einzeltermine in der Beschreibung</p>
                            </div>
                        ) : formData.dates?.length > 0 && (
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Termine</p>
                                <div className="flex flex-wrap gap-2">
                                    {formData.dates.filter(d => d.date).map((d, i) => {
                                        const fmt = d.format || 'praesenz';
                                        const fmtLabel = fmt === 'online' ? 'Online' : fmt === 'hybrid' ? 'Hybrid' : 'Präsenz';
                                        const fmtClass = fmt === 'online' ? 'bg-blue-100 text-blue-700' : fmt === 'hybrid' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700';
                                        return (
                                            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-lg text-sm">
                                                {fmtDate(d.date)}, {d.time_start}–{d.time_end}
                                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${fmtClass}`}>{fmtLabel}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {formData.description && <div><p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Beschreibung</p><div className="legal-html text-sm" dangerouslySetInnerHTML={{ __html: formData.description }} /></div>}
                        {formData.certificate_content && <div><p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Zertifikatsinhalt</p><div className="legal-html text-sm" dangerouslySetInnerHTML={{ __html: formData.certificate_content }} /></div>}
                        {formData.external_link && <InfoRow label="Externer Link" value={formData.external_link} />}
                        {formData.survey_link && <InfoRow label="Umfrage-Link" value={formData.survey_link} />}
                        {formData.organizer_type === 'bgpr' && (
                            <div className="col-span-2">
                                <p className="text-xs text-muted-foreground uppercase tracking-wider">Bescheinigungstyp</p>
                                <p className="text-sm mt-0.5 font-medium">{certTypeLabel}</p>
                            </div>
                        )}
                    </CardContent></Card>
                    {formData.is_in_house && (
                        <Card className="border-indigo-200 bg-indigo-50/30">
                            <CardContent className="pt-6 space-y-3">
                                <div className="flex items-center gap-2">
                                    <Building2 className="h-5 w-5 text-indigo-700" />
                                    <h3 className="font-semibold text-indigo-900">Firmenfortbildung (In-House)</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                    <InfoRow label="Firma" value={formData.in_house_company_name || '—'} />
                                    <InfoRow label="Ansprechpartner" value={formData.in_house_contact_person || '—'} />
                                    <InfoRow label="E-Mail" value={formData.in_house_email || '—'} />
                                    <InfoRow label="Telefon" value={formData.in_house_phone || '—'} />
                                    <InfoRow
                                        label="Preisgestaltung"
                                        value={formData.in_house_price_type === 'flat'
                                            ? `Pauschal ${formatCurrency(formData.in_house_flat_price)}`
                                            : `${formatCurrency(formData.in_house_per_participant_price)} / Teilnehmer`}
                                    />
                                    <InfoRow label="Max. Teilnehmer (Firma)" value={formData.in_house_max_participants || formData.max_participants} />
                                </div>
                                <div className="pt-2 border-t border-indigo-200/60">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Anmeldelink für die Firma</p>
                                    {formData.in_house_signup_token ? (
                                        <div className="flex items-center gap-2">
                                            <Input
                                                readOnly
                                                value={`${window.location.origin}/in-house/${formData.in_house_signup_token}`}
                                                className="text-xs font-mono"
                                                data-testid="in-house-signup-link-display"
                                            />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(`${window.location.origin}/in-house/${formData.in_house_signup_token}`);
                                                        toast.success('Link kopiert');
                                                    } catch { toast.error('Bitte manuell markieren'); }
                                                }}
                                                data-testid="in-house-copy-link-inline"
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-muted-foreground">Kein Signup-Link vorhanden — Kurs speichern, um ihn zu erzeugen.</p>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Über den „In-House"-Button oben können Sie die Rechnungsanschrift prüfen und die Sammelrechnung erstellen.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* ========= EDIT MODE ========= */}
            {(isNew || (activeTab === 'details' && editMode)) && <SeminarForm formData={formData} setFormData={setFormData} handleChange={handleChange} addDate={addDate} removeDate={removeDate} updateDate={updateDate} addDiscount={addDiscount} removeDiscount={removeDiscount} updateDiscount={updateDiscount} isNew={isNew} fetchNextNumber={fetchNextNumber} templates={templates} licenseTypes={licenseTypes} />}

            {/* ========= TEILNEHMER TAB ========= */}
            {!isNew && activeTab === 'participants' && (
                <div className="flex flex-col gap-8">
                    {/* Summary */}
                    <div className="grid grid-cols-4 gap-4">
                        <SummaryCard icon={Users} color="blue" value={confirmedCount} label={`Angemeldet (min. ${formData.min_participants})`} />
                        <SummaryCard icon={UserCheck} color="green" value={attendedCount} label="Vollständig anwesend" />
                        <SummaryCard icon={Receipt} color="amber" value={paidCount} label="Bezahlt" />
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${courseConfirmed ? 'bg-green-100 text-green-600' : minReached ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'}`}>
                                        <CheckCircle className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">{courseConfirmed ? 'Kurs bestätigt' : minReached ? 'Bereit zur Bestätigung' : 'Min. nicht erreicht'}</p>
                                        <p className="text-xs text-muted-foreground">{confirmedCount}/{formData.max_participants} Plätze</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Absage-Banner */}
                    {isCancelled && (
                        <div className="p-4 rounded-lg border bg-red-50 border-red-300" data-testid="course-cancelled-banner">
                            <div className="flex items-start gap-3">
                                <Ban className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-red-900">Kurs wurde abgesagt</p>
                                    {cancellationReason && (
                                        <p className="text-xs text-red-800 mt-0.5">
                                            <span className="font-medium">Grund:</span> {cancellationReason}
                                        </p>
                                    )}
                                    <p className="text-xs text-red-700 mt-1">
                                        Neubuchungen sind gesperrt. Bestehende Buchungen wurden gemäß gewähltem Verfahren verarbeitet.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Confirm course banner */}
                    {!courseConfirmed && !isCancelled && (
                        <div className={`p-4 rounded-lg border flex items-center justify-between gap-3 flex-wrap ${minReached ? 'bg-primary/5 border-primary/20' : 'bg-amber-50 border-amber-200'}`}>
                            <div>
                                <p className="text-sm font-medium">{minReached ? 'Mindestteilnehmerzahl erreicht!' : 'Mindestteilnehmerzahl noch nicht erreicht'}</p>
                                <p className="text-xs text-muted-foreground">{confirmedCount} von {formData.min_participants} erforderlichen Teilnehmern{!minReached && ' — Kurs kann trotzdem bestätigt werden'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => setCancelDialogOpen(true)} data-testid="cancel-course-btn" className="text-red-700 border-red-300 hover:bg-red-50">
                                    <Ban className="h-4 w-4 mr-1" />
                                    Kurs absagen
                                </Button>
                                <Button size="sm" onClick={handleConfirmCourse} data-testid="confirm-course-btn">
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Kurs bestätigen & Zahlungsaufforderungen versenden
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Auch nach Bestätigung soll Absage möglich sein — kleinerer Button */}
                    {courseConfirmed && !isCancelled && (
                        <div className="flex justify-end">
                            <Button size="sm" variant="ghost" onClick={() => setCancelDialogOpen(true)} data-testid="cancel-course-btn-post-confirm" className="text-red-700 hover:bg-red-50">
                                <Ban className="h-4 w-4 mr-1" />
                                Kurs nachträglich absagen
                            </Button>
                        </div>
                    )}

                    {/* Backfill banner: Sichtbar nach Kurs-Bestätigung. Erlaubt es,
                        fehlende offene Rechnungen für confirmed Buchungen rückwirkend
                        anzulegen — relevant für Seminare, die vor dem 05/2026-Fix
                        bestätigt wurden. */}
                    {courseConfirmed && (
                        <div className="p-3 rounded-lg border bg-blue-50/60 border-blue-200 flex items-center justify-between gap-3 flex-wrap" data-testid="backfill-invoices-banner">
                            <div className="text-sm text-blue-900 flex items-start gap-2">
                                <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                                <div>
                                    <span className="font-medium">Kurs bestätigt.</span>{' '}
                                    Falls in der Buchhaltung „Erwartet (Seminargebühren)" für dieses Seminar 0,00 € steht,
                                    sind die offenen Forderungen noch nicht intern erfasst — die Reparatur holt das nach.
                                    <span className="text-xs text-blue-700 block mt-0.5">(Interne Buchführungs-Notiz — es wird keine Rechnung erstellt oder versandt.)</span>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBackfillInvoices}
                                disabled={backfillRunning}
                                data-testid="backfill-invoices-btn"
                                title="Erfasst fehlende offene Forderungen für alle bezahlpflichtigen, bestätigten Teilnehmer (interne Buchführungs-Notiz, keine Rechnung)"
                            >
                                {backfillRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Receipt className="h-3.5 w-3.5 mr-1" />}
                                Buchhaltung reparieren
                            </Button>
                        </div>
                    )}

                    {/* Reparatur-Hinweis: record_type der auto-erstellten Nachweise an
                        seminar.is_first_aid angleichen. Nur sichtbar, sobald mind. ein
                        Teilnehmer eine Lehrgangsbescheinigung erhalten hat. */}
                    {participants.some(p => p.attended) && (
                        <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-start justify-between gap-3" data-testid="resync-training-records-banner">
                            <div className="flex items-start gap-2 text-sm text-amber-900 flex-1">
                                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                    <span className="font-medium">Erste-Hilfe-Typ nachträglich anpassen?</span>{' '}
                                    Falls die Bescheinigungen mit falschem Typ („Regulär" statt „Erste-Hilfe" oder umgekehrt) verbucht
                                    wurden, gleicht die Reparatur sie an die aktuelle Seminar-Einstellung an.
                                    <span className="text-xs text-amber-700 block mt-0.5">Bereits für eine Verlängerung verbrauchte Nachweise bleiben unberührt.</span>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleResyncTrainingRecords}
                                disabled={recordResyncRunning}
                                data-testid="resync-training-records-btn"
                                title="Setzt record_type aller auto-erstellten Nachweise auf seminar.is_first_aid"
                            >
                                {recordResyncRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <HeartPulse className="h-3.5 w-3.5 mr-1" />}
                                Nachweis-Typ angleichen
                            </Button>
                        </div>
                    )}

                    {/* Actions: Filter zuerst (links), dann Aktionen */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground whitespace-nowrap">Filter:</Label>
                            <Select value={participantFilter} onValueChange={setParticipantFilter}>
                                <SelectTrigger className="w-[180px] h-9" data-testid="participant-filter-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all" data-testid="filter-option-all">Alle Teilnehmer</SelectItem>
                                    <SelectItem value="paid" data-testid="filter-option-paid">Bezahlt</SelectItem>
                                    <SelectItem value="partial" data-testid="filter-option-partial">Teilbezahlt</SelectItem>
                                    <SelectItem value="open" data-testid="filter-option-open">Offener Betrag</SelectItem>
                                    <SelectItem value="cancelled" data-testid="filter-option-cancelled">Storniert</SelectItem>
                                    <SelectItem value="referent" data-testid="filter-option-referent">Referent</SelectItem>
                                </SelectContent>
                            </Select>
                            <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="participant-filter-count">
                                {filteredParticipants.length} / {participants.length}
                            </span>
                        </div>
                        <div className="border-l h-6 mx-1" />
                        <Button variant="outline" size="sm" onClick={handleMarkAllAttended} disabled={confirmedCount === attendedCount} data-testid="mark-all-attended">
                            <UserCheck className="h-4 w-4 mr-1" /> Alle anwesend
                        </Button>
                        <div className="border-l h-6 mx-1" />
                        <Button variant="outline" size="sm" onClick={handleDownloadParticipantList} data-testid="download-participant-list">
                            <Printer className="h-4 w-4 mr-1" /> Teilnehmerliste PDF
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleDownloadAttendanceList} data-testid="download-attendance-list">
                            <Printer className="h-4 w-4 mr-1" /> Anwesenheitsliste PDF
                        </Button>
                        {/* Plausibilitäts-Anzeige: Wie wird die Bescheinigung verbucht?
                            Damit der Admin auf einen Blick sieht, ob der Erste-Hilfe-Schalter
                            korrekt gesetzt ist, BEVOR er Bescheinigungen versendet. */}
                        {formData.organizer_type === 'bgpr' && (
                            <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${
                                    formData.is_first_aid
                                        ? 'bg-amber-50 text-amber-800 border-amber-300'
                                        : 'bg-sky-50 text-sky-800 border-sky-200'
                                }`}
                                title={formData.is_first_aid
                                    ? 'Bescheinigungen werden als Erste-Hilfe-Nachweis verbucht (für 1.-Hilfe-Pflicht der Lizenz-Verlängerung). Steuerung über den „Erste-Hilfe-Kurs"-Schalter im Seminar.'
                                    : 'Bescheinigungen werden als reguläre Fortbildung verbucht (UE-Gutschrift). Steuerung über den „Erste-Hilfe-Kurs"-Schalter im Seminar.'}
                                data-testid="cert-booking-type-badge"
                            >
                                {formData.is_first_aid
                                    ? <><HeartPulse className="h-3.5 w-3.5" /> Verbuchung: Erste-Hilfe-Nachweis</>
                                    : <><Award className="h-3.5 w-3.5" /> Verbuchung: Reguläre Fortbildung{Number(formData.ue_count || 0) > 0 ? ` (${formData.ue_count} UE)` : ''}</>}
                            </span>
                        )}
                        <Button variant="outline" size="sm" onClick={handleDownloadBulkCertificates} disabled={confirmedCount === 0} data-testid="download-bulk-certificates" title={confirmedCount === 0 ? 'Es sind keine bestätigten Teilnehmer vorhanden' : 'Alle Teilnehmer-Bescheinigungen als ein PDF zum Drucken'}>
                            <Award className="h-4 w-4 mr-1" /> Bescheinigungen Sammel-PDF
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleBulkRegenerateCerts}
                            disabled={visibleSelected.length === 0 || bulkRegenRunning}
                            data-testid="bulk-regenerate-cert-btn"
                            title={visibleSelected.length === 0 ? 'Erst anwesende Teilnehmer per Checkbox auswählen' : 'Bescheinigungen NEU generieren – Portal + Dropbox aktualisieren, ohne E-Mail-Versand (Prüf-Modus)'}
                            className="border-amber-300 text-amber-800 hover:bg-amber-50"
                        >
                            {bulkRegenRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                            Nur regenerieren (kein Mail-Versand) {visibleSelected.length > 0 ? `(${visibleSelected.length})` : ''}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleBulkSendCertEmails}
                            disabled={visibleSelected.length === 0 || bulkCertMailRunning}
                            data-testid="bulk-send-cert-emails-btn"
                            title={visibleSelected.length === 0 ? 'Erst anwesende Teilnehmer per Checkbox auswählen' : 'Bescheinigungen per E-Mail an Auswahl senden'}
                        >
                            {bulkCertMailRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                            Bescheinigungen an Auswahl senden {visibleSelected.length > 0 ? `(${visibleSelected.length})` : ''}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMailComposerOpen(true)}
                            disabled={visibleSelected.length === 0}
                            data-testid="open-mail-composer-btn"
                            title={visibleSelected.length === 0 ? 'Erst Teilnehmer per Checkbox auswählen' : undefined}
                        >
                            <Mail className="h-4 w-4 mr-1" /> Mail an Teilnehmer {visibleSelected.length > 0 ? `(${visibleSelected.length})` : ''}
                        </Button>
                    </div>

                    {/* Table */}
                    {loadingParticipants ? (
                        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                    ) : participants.length === 0 ? (
                        <Card><CardContent className="py-12 text-center"><Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">Noch keine Teilnehmer angemeldet</p></CardContent></Card>
                    ) : filteredParticipants.length === 0 ? (
                        <Card><CardContent className="py-12 text-center"><Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">Kein Teilnehmer passt auf den Filter „{FILTER_LABELS[participantFilter]}"</p><Button size="sm" variant="outline" className="mt-3" onClick={() => setParticipantFilter('all')} data-testid="reset-participant-filter">Filter zurücksetzen</Button></CardContent></Card>
                    ) : (
                        <Card>
                            <div className="w-full">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b bg-muted/30">
                                            <th className="text-center px-2 py-3 font-medium w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={allFilteredSelected}
                                                    onChange={toggleSelectAllVisible}
                                                    disabled={selectableBookingIds.length === 0}
                                                    className={`w-4 h-4 accent-primary ${selectableBookingIds.length === 0 ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                                                    title={selectableBookingIds.length === 0
                                                        ? 'Keine auswählbaren Teilnehmer (stornierte werden übersprungen)'
                                                        : (allFilteredSelected ? 'Auswahl entfernen' : 'Alle auswählen (stornierte werden übersprungen)')}
                                                    data-testid="participant-select-all"
                                                />
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium">Teilnehmer</th>
                                            <th className="text-left px-4 py-3 font-medium">Status</th>
                                            {seminarDates.map(d => (
                                                <th key={d} className="text-center px-3 py-3 font-medium text-xs">{fmtDate(d)}</th>
                                            ))}
                                            {seminarDates.length === 0 && <th className="text-center px-4 py-3 font-medium">Anwesend</th>}
                                            <th className="text-left px-4 py-3 font-medium">Zahlung</th>
                                            <th className="text-center px-3 py-3 font-medium text-xs">Bescheinigung</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredParticipants.map((p) => (
                                            <tr key={p.booking_id} className={`border-b last:border-0 hover:bg-primary/5 cursor-pointer transition-colors [&>td]:align-middle ${p.status === 'cancelled' ? 'opacity-40' : ''}`} data-testid={`participant-row-${p.booking_id}`} onClick={() => openParticipant(p.booking_id)}>
                                                <td className="px-2 py-5 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedBookingIds.includes(p.booking_id)}
                                                        onChange={() => toggleRowSelected(p.booking_id)}
                                                        disabled={p.status === 'cancelled'}
                                                        className={`w-4 h-4 accent-primary ${p.status === 'cancelled' ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                                                        title={p.status === 'cancelled' ? 'Stornierte Teilnehmer können nicht für Versand ausgewählt werden' : undefined}
                                                        data-testid={`participant-select-${p.booking_id}`}
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <p className="font-medium leading-tight">{p.participant_name || 'Unbekannt'}</p>
                                                    <p className="text-xs text-muted-foreground mt-1 leading-tight">{p.participant_email}</p>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.status === 'confirmed' ? 'bg-green-100 text-green-800' : p.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                                                        {p.status === 'confirmed' ? 'Bestätigt' : p.status === 'cancelled' ? 'Storniert' : p.status}
                                                    </span>
                                                </td>
                                                {seminarDates.map(date => {
                                                    const isPresent = p.attendance?.[date] || false;
                                                    return (
                                                        <td key={date} className="px-3 py-5 text-center" onClick={(e) => e.stopPropagation()}>
                                                            {p.status !== 'cancelled' && (
                                                                <button onClick={() => handleToggleAttendance(p.booking_id, date, isPresent)}
                                                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${isPresent ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-300 hover:bg-gray-200'}`}
                                                                    data-testid={`att-${p.booking_id}-${date}`}>
                                                                    {isPresent ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                                                </button>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                {seminarDates.length === 0 && (
                                                    <td className="px-4 py-5 text-center" onClick={(e) => e.stopPropagation()}>
                                                        {p.status !== 'cancelled' && (
                                                            <button onClick={() => handleToggleAttendance(p.booking_id, null, p.attended)}
                                                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${p.attended ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-300 hover:bg-gray-200'}`}>
                                                                {p.attended ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="px-4 py-5" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        {p.is_referent ? (
                                                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200" data-testid={`referent-badge-${p.booking_id}`}>
                                                                <CheckCircle className="h-3 w-3" /> Referent
                                                            </span>
                                                        ) : p.is_free ? (
                                                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200" data-testid={`free-badge-${p.booking_id}`}>
                                                                <CheckCircle className="h-3 w-3" /> Gebührenfrei
                                                            </span>
                                                        ) : p.payment_status === 'paid' ? (
                                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                                                <CheckCircle className="h-3.5 w-3.5" /> Bezahlt
                                                            </span>
                                                        ) : p.payment_requested ? (
                                                            <span className="text-xs text-amber-600 font-medium">Angefordert</span>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground">offen</span>
                                                        )}
                                                        {!p.is_referent && p.final_price != null && (
                                                            <span className="text-xs font-mono text-muted-foreground">
                                                                {Number(p.final_price).toFixed(2)} €
                                                            </span>
                                                        )}
                                                        {!p.is_referent && p.status !== 'cancelled' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => openPriceEdit(p)}
                                                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                                                title="Preis korrigieren"
                                                                data-testid={`edit-price-${p.booking_id}`}
                                                            >
                                                                <Pencil className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-5 text-center" data-testid={`cert-status-cell-${p.booking_id}`}>
                                                    <div className="flex items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                        {p.certificate_generated ? (
                                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700" title="Bescheinigung wurde bereits ausgestellt und an den Teilnehmer versandt.">
                                                                <CheckCircle className="h-3.5 w-3.5" /> Versandt
                                                            </span>
                                                        ) : p.status === 'cancelled' ? (
                                                            <span className="text-xs text-muted-foreground">—</span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700" title="Noch keine Bescheinigung. Wird mit dem nächsten Bescheinigungs-Mailversand erzeugt.">
                                                                <AlertTriangle className="h-3.5 w-3.5" /> Ausstehend
                                                            </span>
                                                        )}
                                                        {p.attended && p.status !== 'cancelled' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePreviewCert(p)}
                                                                disabled={previewCertId === p.booking_id}
                                                                title="Frische PDF direkt im Browser laden (kein Mailversand, kein Dropbox — nur zum Prüfen)"
                                                                className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-primary/10 text-primary disabled:opacity-40"
                                                                data-testid={`preview-cert-btn-${p.booking_id}`}
                                                            >
                                                                {previewCertId === p.booking_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {/* ========= WARTELISTE ========= */}
            {!isNew && activeTab === 'waiting-list' && (
                <div className="space-y-6 max-w-5xl" data-testid="waiting-list-tab">
                    <Card>
                        <CardContent className="pt-6 space-y-4">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <h3 className="text-base font-bold flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-amber-600" />
                                        Warteliste
                                    </h3>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Mitglieder, die sich für das ausgebuchte Seminar angemeldet haben.
                                        Bei Stornierung einer aktiven Buchung wird der nächste Eintrag automatisch
                                        per E-Mail gefragt (48 h Frist).
                                    </p>
                                </div>
                                <Button size="sm" variant="outline" onClick={loadWaitingList} disabled={loadingWaitingList} data-testid="reload-waiting-list">
                                    {loadingWaitingList ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                                    Aktualisieren
                                </Button>
                            </div>

                            {loadingWaitingList ? (
                                <div className="py-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
                            ) : waitingList.length === 0 ? (
                                <div className="py-10 text-center text-sm text-muted-foreground border border-dashed rounded-lg" data-testid="waiting-list-empty">
                                    Aktuell steht niemand auf der Warteliste.
                                </div>
                            ) : (
                                <div className="overflow-hidden rounded-lg border">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                                            <tr>
                                                <th className="px-4 py-3 text-left w-12">#</th>
                                                <th className="px-4 py-3 text-left">Teilnehmer</th>
                                                <th className="px-4 py-3 text-left">Eingegangen</th>
                                                <th className="px-4 py-3 text-left">Status</th>
                                                <th className="px-4 py-3 text-right">Aktion</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {waitingList.map((w) => {
                                                const isPending = w.status === 'pending_confirmation';
                                                return (
                                                    <tr key={w.booking_id} data-testid={`waiting-list-row-${w.booking_id}`}
                                                        className="hover:bg-muted/40 cursor-pointer"
                                                        onClick={() => setWaitingDetailId(w.booking_id)}
                                                    >
                                                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{w.position}</td>
                                                        <td className="px-4 py-3">
                                                            <div className="font-medium">{w.participant_name || '—'}</div>
                                                            <div className="text-xs text-muted-foreground">{w.participant_email}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                                            {w.created_at ? new Date(w.created_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {isPending ? (
                                                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                                                    <Clock className="h-3 w-3" />
                                                                    Annahme läuft{w.confirmation_expires_at ? ` · bis ${new Date(w.confirmation_expires_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                                                                    Wartet
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                                            <div className="inline-flex items-center gap-1">
                                                                {!isPending && (
                                                                    <Button size="sm" variant="outline"
                                                                        onClick={() => handlePromote(w.booking_id)}
                                                                        data-testid={`promote-waiting-${w.booking_id}`}
                                                                        title="Annehmen-/Ablehnen-Mail manuell verschicken (48 h Frist)"
                                                                    >
                                                                        <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />
                                                                        Promoten
                                                                    </Button>
                                                                )}
                                                                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    onClick={() => handleRemoveFromWaitingList(w.booking_id)}
                                                                    data-testid={`remove-waiting-${w.booking_id}`}
                                                                    title="Vom Warteliste entfernen"
                                                                >
                                                                    <Trash className="h-3.5 w-3.5" />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Mail-Composer Dialog */}
            <ParticipantMailComposer
                open={mailComposerOpen}
                onOpenChange={setMailComposerOpen}
                seminar={{ id, title: formData.title, survey_link: formData.survey_link }}
                recipients={selectedRecipients.filter(r => filteredBookingIds.includes(r.booking_id))}
                onSent={() => {
                    setSelectedBookingIds([]);
                    loadParticipants();
                }}
            />

            {/* Teilnehmerlisten-Export mit Feldauswahl */}
            <ParticipantListExportDialog
                seminarId={id}
                courseNumber={formData.course_number}
                open={participantExportOpen}
                onOpenChange={setParticipantExportOpen}
            />

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>                <DialogContent>
                    <DialogHeader><DialogTitle>Fortbildung löschen?</DialogTitle></DialogHeader>
                    <p className="text-muted-foreground">Möchten Sie "{formData.title}" wirklich löschen?</p>
                    <DeleteImpactNotice impact={deleteImpact} />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={!!deleteImpact?.paid_invoice_numbers?.length} data-testid="confirm-delete-seminar">Löschen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preis-Korrektur-Dialog */}
            <Dialog open={!!priceEditBooking} onOpenChange={(o) => { if (!o) { setPriceEditBooking(null); setPriceInput(''); } }}>
                <DialogContent data-testid="price-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>Preis korrigieren</DialogTitle>
                    </DialogHeader>
                    {priceEditBooking && (
                        <div className="space-y-4">
                            <div className="text-sm">
                                <p className="font-medium">{priceEditBooking.name}</p>
                                <p className="text-muted-foreground">
                                    Bisheriger Preis: <span className="font-mono">{Number(priceEditBooking.old_price).toFixed(2)} €</span>
                                </p>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="new-price">Neuer Seminarpreis (€)</Label>
                                <Input
                                    id="new-price"
                                    type="text"
                                    inputMode="decimal"
                                    value={priceInput}
                                    onChange={(e) => setPriceInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSavePrice(); }}
                                    autoFocus
                                    data-testid="price-edit-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Komma oder Punkt als Dezimaltrennzeichen. Beispiel: <span className="font-mono">49,50</span>
                                </p>
                            </div>
                            {priceEditBooking.payment_status === 'paid' && (
                                <div className="text-xs p-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                                    ⚠️ Diese Buchung wurde bereits bezahlt. Die ursprüngliche Rechnung bleibt unverändert.
                                    Falls eine Differenz zurückgezahlt oder nachgefordert werden muss, bitte separat buchen.
                                </div>
                            )}
                            {priceEditBooking.payment_status === 'requested' && (
                                <div className="text-xs p-2.5 rounded-md bg-blue-50 border border-blue-200 text-blue-800">
                                    ℹ️ Es wurde bereits eine Zahlungsaufforderung versandt. Die offene Rechnung wird automatisch
                                    auf den neuen Betrag aktualisiert — Sie können dem Teilnehmer anschließend
                                    über „Zahlungsaufforderung erneut versenden" die korrigierte Aufforderung schicken.
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setPriceEditBooking(null); setPriceInput(''); }} disabled={savingPrice}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSavePrice} disabled={savingPrice} data-testid="price-edit-save">
                            {savingPrice ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Warteliste-Detail-Drawer */}
            <WaitingListDetailDialog
                bookingId={waitingDetailId}
                onClose={() => setWaitingDetailId(null)}
                onPromote={async (bid) => { await handlePromote(bid); setWaitingDetailId(null); }}
                onRemove={async (bid) => { await handleRemoveFromWaitingList(bid); setWaitingDetailId(null); }}
            />

            {/* Kurs-Absage-Dialog */}
            <CancelCourseDialog
                open={cancelDialogOpen}
                onOpenChange={setCancelDialogOpen}
                seminarId={id}
                onCancelled={() => { loadSeminar(); loadParticipants(); }}
            />

            {/* In-House-Dialog */}
            <InHouseDialog
                open={inHouseDialogOpen}
                onOpenChange={setInHouseDialogOpen}
                seminar={formData ? {
                    ...formData,
                    id,
                    in_house_company: {
                        name: formData.in_house_company_name,
                        contact_person: formData.in_house_contact_person,
                        email: formData.in_house_email,
                        phone: formData.in_house_phone,
                    },
                } : null}
                onUpdated={loadSeminar}
            />
        </AdminLayout>
    );
}

// === Sub-components ===

function InfoRow({ label, value, mono, className }) {
    if (!value) return null;
    return (
        <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className={`text-sm mt-0.5 ${mono ? 'font-mono' : ''} ${className || ''}`}>{value}</p>
        </div>
    );
}

function SummaryCard({ icon: Icon, color, value, label }) {
    const colors = { blue: 'bg-blue-100 text-blue-600', green: 'bg-green-100 text-green-600', amber: 'bg-amber-100 text-amber-600' };
    return (
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${colors[color]}`}><Icon className="h-5 w-5" /></div>
            <div><p className="text-2xl font-bold">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div>
        </div></CardContent></Card>
    );
}

function SeminarForm({ formData, setFormData, handleChange, addDate, removeDate, updateDate, addDiscount, removeDiscount, updateDiscount, isNew, fetchNextNumber, templates = [], licenseTypes = [] }) {
    // Templates usable as certificate (license or certificate type)
    const certTemplates = templates.filter(t => ['license', 'certificate'].includes(t.type) && t.is_active !== false);
    // Auto-pick helper: first template of given type, else undefined
    const autoPick = (type) => certTemplates.find(t => t.type === type)?.id;
    return (
        <div className="grid grid-cols-2 gap-4 max-w-4xl">
            <div className="space-y-2 col-span-2"><Label>Titel *</Label><Input name="title" value={formData.title} onChange={handleChange} required data-testid="seminar-title" /></div>
            <div className="space-y-2"><Label>Untertitel</Label><Input name="subtitle" value={formData.subtitle} onChange={handleChange} data-testid="seminar-subtitle" /></div>
            <div className="space-y-2"><Label>Kursnummer</Label><Input name="course_number" value={formData.course_number} onChange={handleChange} className="font-mono" data-testid="seminar-course-number" /></div>
            <div className="space-y-2 col-span-2"><Label>Beschreibung</Label>
                <EmailEditor content={formData.description} onChange={(html) => setFormData(f => ({ ...f, description: html }))} showPlaceholders={false} />
            </div>

            <div className="col-span-2 p-4 bg-muted/50 rounded-lg border space-y-3">
                <Label className="font-semibold">Veranstalter *</Label>
                <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="org" value="bgpr" checked={formData.organizer_type === 'bgpr'}
                            onChange={async () => { const n = isNew ? await fetchNextNumber() : formData.course_number; setFormData(f => ({ ...f, organizer_type: 'bgpr', organizer: 'BGPR-Akademie', organizer_name: '', external_link: '', course_number: n })); }}
                            className="w-4 h-4 accent-red-600" data-testid="organizer-type-bgpr" />
                        <span className="text-sm font-medium">BGPR-Akademie</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="org" value="external" checked={formData.organizer_type === 'external'}
                            onChange={() => setFormData(f => ({ ...f, organizer_type: 'external', organizer: '', course_number: '' }))}
                            className="w-4 h-4 accent-blue-600" data-testid="organizer-type-external" />
                        <span className="text-sm font-medium">Externer Anbieter</span>
                    </label>
                </div>
                {formData.organizer_type === 'external' && (
                    <div className="space-y-3 pt-2">
                        <div className="space-y-2"><Label>Name des Veranstalters *</Label><Input value={formData.organizer_name} onChange={(e) => setFormData(f => ({ ...f, organizer_name: e.target.value, organizer: e.target.value }))} required data-testid="seminar-organizer-name" /></div>
                        <div className="space-y-2"><Label>Link zur Anmeldung *</Label><Input name="external_link" type="url" value={formData.external_link} onChange={handleChange} placeholder="https://..." required data-testid="seminar-external-link" /></div>
                    </div>
                )}
            </div>

            {formData.organizer_type === 'bgpr' && (
                <>
                {/* In-House / Firmenfortbildung — analog zur Veranstalter-Unterscheidung */}
                <div className="col-span-2 p-4 bg-indigo-50/40 rounded-lg border border-indigo-200/60 space-y-3">
                    <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="course_kind"
                                checked={!formData.is_in_house}
                                onChange={() => setFormData(f => ({ ...f, is_in_house: false }))}
                                className="w-4 h-4 accent-emerald-600"
                                data-testid="course-kind-public"
                            />
                            <span className="text-sm font-medium">Öffentliche Fortbildung</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="course_kind"
                                checked={!!formData.is_in_house}
                                onChange={() => setFormData(f => ({ ...f, is_in_house: true, is_published: false, in_house_max_participants: f.in_house_max_participants || String(f.max_participants || '') }))}
                                className="w-4 h-4 accent-indigo-600"
                                data-testid="course-kind-in-house"
                            />
                            <span className="text-sm font-medium">Firmenfortbildung (In-House)</span>
                        </label>
                    </div>
                    {formData.is_in_house && (
                        <div className="space-y-3 pt-2 border-t border-indigo-200/50">
                            <p className="text-xs text-indigo-900/80">
                                In-House-Kurse sind <strong>nicht öffentlich buchbar</strong>. Nach dem Speichern erhalten Sie einen Signup-Link,
                                den Sie an die Firma senden — diese meldet ihre Mitarbeiter selbst an. Sammelrechnung erfolgt an die Firma.
                            </p>
                            <div className="space-y-2">
                                <Label>Firma *</Label>
                                <Input value={formData.in_house_company_name || ''} onChange={(e) => setFormData(f => ({ ...f, in_house_company_name: e.target.value }))} placeholder="Firma GmbH" data-testid="in-house-company-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Ansprechpartner</Label>
                                    <Input value={formData.in_house_contact_person || ''} onChange={(e) => setFormData(f => ({ ...f, in_house_contact_person: e.target.value }))} data-testid="in-house-contact-person" />
                                </div>
                                <div className="space-y-2">
                                    <Label>E-Mail</Label>
                                    <Input type="email" value={formData.in_house_email || ''} onChange={(e) => setFormData(f => ({ ...f, in_house_email: e.target.value }))} data-testid="in-house-email" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Telefon</Label>
                                <Input value={formData.in_house_phone || ''} onChange={(e) => setFormData(f => ({ ...f, in_house_phone: e.target.value }))} data-testid="in-house-phone" />
                            </div>
                            <div className="border-t border-indigo-200/50 pt-3 space-y-2">
                                <Label className="text-sm font-semibold">Preisgestaltung *</Label>
                                <div className="flex flex-col gap-1 text-sm">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="ih_price_type" checked={formData.in_house_price_type === 'flat'} onChange={() => setFormData(f => ({ ...f, in_house_price_type: 'flat' }))} className="w-4 h-4 accent-indigo-600" data-testid="in-house-price-flat" />
                                        Pauschalpreis (unabhängig von TN-Zahl)
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="ih_price_type" checked={formData.in_house_price_type === 'per_participant'} onChange={() => setFormData(f => ({ ...f, in_house_price_type: 'per_participant' }))} className="w-4 h-4 accent-indigo-600" data-testid="in-house-price-per" />
                                        Preis pro Teilnehmer
                                    </label>
                                </div>
                                {formData.in_house_price_type === 'flat' ? (
                                    <div className="space-y-2">
                                        <Label>Pauschalpreis (netto, EUR) *</Label>
                                        <Input type="number" step="0.01" min="0" value={formData.in_house_flat_price ?? ''} onChange={(e) => setFormData(f => ({ ...f, in_house_flat_price: e.target.value }))} placeholder="1500.00" data-testid="in-house-flat-price" />
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <Label>Preis pro Teilnehmer (netto, EUR) *</Label>
                                        <Input type="number" step="0.01" min="0" value={formData.in_house_per_participant_price ?? ''} onChange={(e) => setFormData(f => ({ ...f, in_house_per_participant_price: e.target.value }))} placeholder="100.00" data-testid="in-house-per-price" />
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label>Max. Teilnehmerzahl (Firmen-Override, optional)</Label>
                                    <Input type="number" min="1" value={formData.in_house_max_participants ?? ''} onChange={(e) => setFormData(f => ({ ...f, in_house_max_participants: e.target.value }))} placeholder={String(formData.max_participants || '')} data-testid="in-house-max-participants" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-span-2 grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Veranstaltungsart *</Label>
                        <Select value={formData.event_category} onValueChange={v => {
                            const updates = { event_category: v };
                            if (v === 'ausbildung' && !formData.training_type) updates.training_type = 'vollausbildung';
                            if (v === 'ausbildung') updates.certificate_type = autoPick('license') || 'keine';
                            if (v !== 'ausbildung') { updates.training_type = ''; updates.requires_prerequisites = false; updates.certificate_type = autoPick('certificate') || 'keine'; }
                            setFormData(f => ({ ...f, ...updates }));
                        }}><SelectTrigger data-testid="seminar-event-category"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fortbildung">Fortbildung</SelectItem><SelectItem value="weiterbildung">Weiterbildung</SelectItem><SelectItem value="ausbildung">Ausbildung</SelectItem></SelectContent></Select></div>
                    <div className="space-y-2"><Label>Veranstaltungsformat *</Label>
                        <Select value={formData.event_format} onValueChange={v => setFormData(f => ({ ...f, event_format: v }))}><SelectTrigger data-testid="seminar-event-format"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="praesenz">Präsenz</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem><SelectItem value="online">Online</SelectItem></SelectContent></Select></div>
                </div>
                {formData.event_category === 'ausbildung' && (
                    <div className="col-span-2 p-4 bg-amber-50/50 rounded-lg border border-amber-200/50 space-y-4">
                        <Label className="font-semibold">Ausbildungsdetails</Label>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Ausbildungstyp *</Label>
                                <Select value={formData.training_type || 'vollausbildung'} onValueChange={v => {
                                    setFormData(f => ({ ...f, training_type: v, requires_prerequisites: v === 'verkuerzt' }));
                                }}><SelectTrigger data-testid="seminar-training-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="vollausbildung">Vollausbildung (120 UE)</SelectItem><SelectItem value="verkuerzt">Verkürzte Ausbildung</SelectItem></SelectContent></Select></div>
                            <div className="space-y-2"><Label>Zu vergebende Lizenz</Label>
                                <Select value={formData.training_license_type_id || '__none__'} onValueChange={v => {
                                    setFormData(f => ({ ...f, training_license_type_id: v === '__none__' ? null : v }));
                                }}>
                                    <SelectTrigger data-testid="seminar-training-license-type"><SelectValue placeholder="Keine Lizenzart" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— Keine Lizenz ausstellen —</SelectItem>
                                        {(licenseTypes || []).filter(lt => !lt.archived).map(lt => (
                                            <SelectItem key={lt.id} value={lt.id}>{lt.name}{lt.template_type ? '' : ' (⚠ keine PDF-Vorlage)'}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">Wird nach Kursabschluss den Teilnehmern automatisch ausgestellt. Lizenzarten unter Einstellungen → Lizenzarten verwalten.</p>
                            </div>
                        </div>
                        {formData.training_type === 'verkuerzt' && (
                            <div className="flex items-center gap-2 p-3 bg-amber-100/50 rounded-lg">
                                <input type="checkbox" checked={formData.requires_prerequisites} onChange={e => setFormData(f => ({ ...f, requires_prerequisites: e.target.checked }))} className="w-4 h-4 accent-amber-600" />
                                <Label className="text-sm">Vorkenntnisse bei Anmeldung abfragen (Pflicht-Checkboxen)</Label>
                            </div>
                        )}
                    </div>
                )}
                <div className="col-span-2 p-4 bg-blue-50/40 rounded-lg border border-blue-200/50 space-y-2">
                    <Label className="font-semibold text-sm">Externe Anmeldung (optional)</Label>
                    <p className="text-xs text-muted-foreground">Falls die Anmeldung NICHT über die BGPR-Akademie erfolgen soll (z.B. über eine separate App wie „Wochenseminar Ruhpolding"), hier die Ziel-URL angeben. Teilnehmer werden direkt dorthin geleitet, statt sich hier anzumelden.</p>
                    <Input name="external_link" type="url" value={formData.external_link} onChange={handleChange} placeholder="https://... (leer lassen für interne Anmeldung)" data-testid="seminar-bgpr-external-link" />
                </div>
                </>
            )}

            <div className="col-span-2 space-y-3">
                {/* Termin-Modus-Umschalter */}
                <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20">
                    <input
                        type="checkbox"
                        id="date-mode-range"
                        checked={formData.date_mode === 'range'}
                        onChange={(e) => setFormData({ ...formData, date_mode: e.target.checked ? 'range' : 'individual' })}
                        className="mt-1"
                        data-testid="date-mode-range-toggle"
                    />
                    <label htmlFor="date-mode-range" className="text-sm cursor-pointer leading-snug">
                        <span className="font-medium">Termine als Zeitraum eingeben</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                            Aktivieren, wenn statt einzelner Termine ein Zeitraum von/bis gespeichert werden soll
                            (typisch für externe Angebote — Einzeltermine in der Beschreibung).
                        </span>
                    </label>
                </div>

                {formData.date_mode === 'range' ? (
                    <div className="p-3 bg-muted/30 rounded-lg border space-y-2" data-testid="date-range-block">
                        <Label className="font-semibold">Zeitraum *</Label>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1 space-y-1">
                                <Label className="text-xs">Beginn</Label>
                                <Input
                                    type="date"
                                    value={formData.date_range_start}
                                    onChange={(e) => setFormData({ ...formData, date_range_start: e.target.value })}
                                    required
                                    data-testid="date-range-start"
                                />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Label className="text-xs">Ende</Label>
                                <Input
                                    type="date"
                                    value={formData.date_range_end}
                                    onChange={(e) => setFormData({ ...formData, date_range_end: e.target.value })}
                                    required
                                    data-testid="date-range-end"
                                />
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Auf der Seminarseite, in Bestätigungsmails und auf Bescheinigungen erscheint der Zeitraum kompakt
                            (z.&nbsp;B. <code className="text-[10px] bg-gray-100 px-1 rounded">01.06.–15.06.2026</code>). Detaillierte Einzeltermine
                            bitte in der Seminarbeschreibung benennen.
                        </p>
                    </div>
                ) : (
                    <>
                <div className="flex items-center justify-between"><Label className="font-semibold">Termine *</Label><Button type="button" variant="outline" size="sm" onClick={addDate} data-testid="add-date-button"><CalendarPlus className="h-3.5 w-3.5 mr-1.5" /> Termin hinzufügen</Button></div>
                {formData.dates.map((d, i) => (
                    <div key={i} className="p-3 bg-muted/30 rounded-lg border space-y-2" data-testid={`date-row-${i}`}>
                        <div className="flex items-end gap-2">
                            <div className="flex-1 space-y-1"><Label className="text-xs">Datum</Label><Input type="date" value={d.date} onChange={e => updateDate(i, 'date', e.target.value)} required data-testid={`date-${i}`} /></div>
                            <div className="w-28 space-y-1"><Label className="text-xs">Beginn</Label><Input type="time" value={d.time_start} onChange={e => updateDate(i, 'time_start', e.target.value)} data-testid={`time-start-${i}`} /></div>
                            <div className="w-28 space-y-1"><Label className="text-xs">Ende</Label><Input type="time" value={d.time_end} onChange={e => updateDate(i, 'time_end', e.target.value)} data-testid={`time-end-${i}`} /></div>
                            <div className="w-32 space-y-1">
                                <Label className="text-xs">Format</Label>
                                <Select value={d.format || 'praesenz'} onValueChange={v => updateDate(i, 'format', v)}>
                                    <SelectTrigger data-testid={`date-format-${i}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="praesenz">Präsenz</SelectItem>
                                        <SelectItem value="hybrid">Hybrid</SelectItem>
                                        <SelectItem value="online">Online</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {formData.dates.length > 1 && <Button type="button" variant="ghost" size="sm" className="text-red-500 shrink-0" onClick={() => removeDate(i)}><X className="h-4 w-4" /></Button>}
                        </div>
                        {(d.format === 'online' || d.format === 'hybrid') && (
                            <div className="space-y-1">
                                <Label className="text-xs">Meeting-Link (wird nach Zahlungseingang automatisch verschickt)</Label>
                                <Input type="url" value={d.meeting_link || ''} onChange={e => updateDate(i, 'meeting_link', e.target.value)} placeholder="https://meet.google.com/... oder https://zoom.us/..." data-testid={`meeting-link-${i}`} />
                            </div>
                        )}
                    </div>
                ))}
                    </>
                )}
            </div>

            <div className="space-y-2 col-span-2"><Label>Ort *</Label><Input name="location" value={formData.location} onChange={handleChange} required data-testid="seminar-location" /></div>
            <div className="space-y-2"><Label>UE</Label><Input name="ue_count" type="number" min="1" value={formData.ue_count} onChange={handleChange} data-testid="seminar-ue-count" /></div>
            <div className="space-y-2"><Label>Seminargebühr (EUR)</Label><Input name="price" type="number" min="0" step="0.01" value={formData.price} onChange={handleChange} data-testid="seminar-price" /></div>
            <div className="space-y-2"><Label>Min. Teilnehmer</Label><Input name="min_participants" type="number" min="1" value={formData.min_participants} onChange={handleChange} /></div>
            <div className="space-y-2"><Label>Max. Teilnehmer</Label><Input name="max_participants" type="number" min="1" value={formData.max_participants} onChange={handleChange} /></div>
            <div className="space-y-2 col-span-2">
                <Label className="flex items-center gap-2">
                    Anmeldeschluss
                    <span className="text-[11px] font-normal text-gray-500">(optional · Tage vor Seminarbeginn · leer = bis Seminartag möglich)</span>
                </Label>
                <div className="flex items-center gap-2">
                    <Input
                        name="registration_deadline_days"
                        type="number"
                        min="0"
                        max="365"
                        step="1"
                        placeholder="z.B. 7"
                        value={formData.registration_deadline_days ?? ''}
                        onChange={handleChange}
                        className="max-w-[120px]"
                        data-testid="seminar-registration-deadline-days"
                    />
                    <span className="text-sm text-gray-600">Tage vor Seminarbeginn</span>
                </div>
                <p className="text-[11px] text-gray-500">
                    Beim Kopieren eines Seminars wird dieser Wert übernommen und passt sich automatisch an die neuen Termine an.
                </p>
            </div>
            <div className="space-y-2 col-span-2">
                <Label>Referenten</Label>
                <ReferentMultiSelect
                    selectedIds={formData.referent_ids || []}
                    onChange={(ids) => setFormData(p => ({ ...p, referent_ids: ids }))}
                    fallbackText={formData.instructor_name}
                />
            </div>

            <div className="col-span-2 space-y-3">
                <div className="flex items-center justify-between"><Label className="font-semibold">Rabatte</Label><Button type="button" variant="outline" size="sm" onClick={addDiscount}><Tag className="h-3.5 w-3.5 mr-1.5" /> Rabatt</Button></div>
                {formData.discounts.map((d, i) => (
                    <div key={i} className="p-3 bg-emerald-50/50 rounded-lg border border-emerald-200/50 space-y-2">
                        <div className="flex items-start gap-2">
                            <div className="flex-1 space-y-1"><Label className="text-xs">Bezeichnung</Label><Input value={d.name} onChange={e => updateDiscount(i, 'name', e.target.value)} /></div>
                            <div className="w-28 space-y-1"><Label className="text-xs">Wert</Label><Input type="number" min="0" step="0.01" value={d.discount_value} onChange={e => updateDiscount(i, 'discount_value', parseFloat(e.target.value) || 0)} /></div>
                            <div className="w-32 space-y-1"><Label className="text-xs">Typ</Label><Select value={d.discount_type} onValueChange={v => updateDiscount(i, 'discount_type', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="euro">Euro</SelectItem><SelectItem value="percent">Prozent</SelectItem></SelectContent></Select></div>
                            <Button type="button" variant="ghost" size="sm" className="text-red-500 mt-5" onClick={() => removeDiscount(i)}><X className="h-4 w-4" /></Button>
                        </div>
                        <div className="space-y-1"><Label className="text-xs">Beschreibung (optional)</Label><Input value={d.description || ''} onChange={e => updateDiscount(i, 'description', e.target.value)} placeholder="z.B. Für Mitgliedsvereine der BGPR" /></div>
                        <div className="flex items-center gap-2 pt-1">
                            <input type="checkbox" id={`disc-verein-${i}`} checked={!!d.requires_verein_name} onChange={e => updateDiscount(i, 'requires_verein_name', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                            <Label htmlFor={`disc-verein-${i}`} className="text-xs cursor-pointer">Vereinsname bei Anmeldung abfragen (wird ins Teilnehmer-Profil als „tätig für:" übernommen)</Label>
                        </div>
                    </div>
                ))}
            </div>

            {formData.organizer_type === 'bgpr' && (
                <div className="space-y-2 col-span-2"><Label>Zertifikatsinhalt</Label>
                    <EmailEditor content={formData.certificate_content} onChange={(html) => setFormData(f => ({ ...f, certificate_content: html }))} showPlaceholders={false} />
                </div>
            )}

            {formData.organizer_type === 'external' && (
                <div className="col-span-2 p-3 rounded-lg border border-amber-200 bg-amber-50/60 text-sm text-amber-900" data-testid="external-no-certificate-hint">
                    <strong>Externes Angebot:</strong> Es wird keine BGPR-Bescheinigung ausgestellt — der externe Anbieter ist für Bescheinigungen verantwortlich. Bescheinigungs-Vorlagen, -Inhalte und der Download-Button im Teilnehmer-Portal werden für dieses Seminar deaktiviert.
                </div>
            )}

            <div className="col-span-2 p-4 bg-blue-50/40 rounded-lg border border-blue-200/50 space-y-2">
                <Label className="font-semibold text-sm">Umfrage-/Feedback-Link (optional)</Label>
                <p className="text-xs text-muted-foreground">
                    Wird beim Teilnehmer-Mail-Versand optional per Toggle eingefügt (Google Forms, Typeform, Microsoft Forms …). Leer lassen, wenn keine Umfrage geplant.
                </p>
                <Input name="survey_link" type="url" value={formData.survey_link} onChange={handleChange} placeholder="https://forms.google.com/…" data-testid="seminar-survey-link" />
            </div>

            {formData.organizer_type === 'bgpr' && (
                <div className="space-y-2 col-span-2">
                    <Label>Bescheinigungstyp</Label>
                    <Select value={formData.certificate_type || 'keine'} onValueChange={v => setFormData(f => ({ ...f, certificate_type: v }))}>
                        <SelectTrigger data-testid="seminar-certificate-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="keine">Keine Bescheinigung</SelectItem>
                            {certTemplates.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                    {t.name}{t.type === 'license' ? ' (Lizenz)' : t.type === 'certificate' ? ' (Lehrgangsbescheinigung)' : ''}
                                </SelectItem>
                            ))}
                            {/* Legacy fallback so existing seminars keep a valid value in the Select */}
                            {['teilnahmebescheinigung', 'herzgruppenleiter_lizenz'].includes(formData.certificate_type) && (
                                <SelectItem value={formData.certificate_type}>
                                    {formData.certificate_type === 'herzgruppenleiter_lizenz' ? 'Herzgruppenleiter-Lizenz (alt)' : 'Teilnahmebescheinigung (alt)'}
                                </SelectItem>
                            )}
                        </SelectContent>
                    </Select>
                    {certTemplates.length === 0 && (
                        <p className="text-xs text-muted-foreground">Bitte zuerst unter <strong>Einstellungen → PDF-Vorlagen</strong> eine Vorlage anlegen.</p>
                    )}
                </div>
            )}

            {formData.organizer_type === 'bgpr' && (
                <div className="col-span-2 p-4 rounded-lg border bg-amber-50/30 border-amber-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <Switch
                            checked={!!formData.is_first_aid}
                            onCheckedChange={c => setFormData(f => ({ ...f, is_first_aid: c }))}
                            data-testid="seminar-first-aid-switch"
                        />
                        <div className="flex-1">
                            <p className="font-medium text-sm">Erste-Hilfe-Kurs</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Aktivieren, wenn dieses Seminar bei Teilnehmern als <strong>1.-Hilfe-Nachweis</strong> für die
                                Lizenz-Verlängerung gilt (statt als reguläre Fortbildung). Beeinflusst die Verbuchung im
                                Mitgliederprofil beim Bescheinigungs-Versand. Bescheinigungs-Vorlage bleibt unverändert.
                            </p>
                        </div>
                    </label>
                </div>
            )}

            <div className="flex items-center gap-2 col-span-2 pb-4"><Switch id="is_published" checked={formData.is_published} onCheckedChange={c => setFormData(f => ({ ...f, is_published: c }))} data-testid="seminar-is-published" /><Label htmlFor="is_published">Veröffentlicht</Label></div>
        </div>
    );
}



// === Warteliste-Detail-Drawer ===
const TYPE_LABELS = {
    confirmation: 'Buchungsbestätigung',
    waiting_list_added: 'Warteliste – aufgenommen',
    waiting_list_promoted: 'Warteliste – Annehmen-Mail',
    payment_request: 'Zahlungsaufforderung',
    payment_confirmation: 'Zahlungsbeleg',
    meeting_link: 'Meeting-Link',
    renewal_due: 'Verlängerungs-Hinweis',
    license_renewed: 'Lizenz verlängert',
    newsletter: 'Newsletter',
    custom: 'Manuelle Mail',
    participant_info: 'Info-Mail',
    participant_thanks: 'Dankeschön-Mail',
};

function WaitingListDetailDialog({ bookingId, onClose, onPromote, onRemove }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const open = !!bookingId;

    useEffect(() => {
        if (!bookingId) { setData(null); return; }
        let cancelled = false;
        setLoading(true);
        bookingApi.getDetail(bookingId)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { if (!cancelled) toast.error(e.response?.data?.detail || 'Detail konnte nicht geladen werden'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [bookingId]);

    const booking = data?.booking || {};
    const participant = data?.participant || {};
    const logs = data?.email_logs || [];
    const isPending = booking.status === 'pending_confirmation';
    const expires = booking.confirmation_expires_at;
    const promotedAt = booking.promoted_at;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-600" />
                        Warteliste-Eintrag
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
                ) : !data ? null : (
                    <div className="space-y-5">
                        {/* Stammdaten */}
                        <div className="rounded-lg border p-4 bg-muted/30">
                            <div className="text-base font-semibold">
                                {`${participant.first_name || ''} ${participant.last_name || ''}`.trim() || booking.participant_name || '—'}
                            </div>
                            <div className="text-sm text-muted-foreground">{participant.email || booking.participant_email}</div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Eingegangen</div>
                                    <div>{booking.created_at ? new Date(booking.created_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Status</div>
                                    <div>
                                        {isPending ? (
                                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                                <Clock className="h-3 w-3" /> Annahme läuft
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                                                Wartet
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {promotedAt && (
                                    <div>
                                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Promotet am</div>
                                        <div>{new Date(promotedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                                    </div>
                                )}
                                {expires && (
                                    <div>
                                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Frist</div>
                                        <div>{new Date(expires).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Mail-Historie */}
                        <div>
                            <h4 className="text-sm font-semibold mb-2">E-Mail-Verlauf zu dieser Buchung</h4>
                            {logs.length === 0 ? (
                                <p className="text-sm text-muted-foreground border border-dashed rounded-lg px-4 py-6 text-center">
                                    Bislang wurde noch keine E-Mail zu dieser Buchung versendet.
                                </p>
                            ) : (
                                <ul className="divide-y border rounded-lg">
                                    {logs.map((log) => (
                                        <li key={log.id} className="px-4 py-3 flex items-start gap-3">
                                            <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${log.status === 'sent' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{log.subject || '(ohne Betreff)'}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[11px]">
                                                        {TYPE_LABELS[log.template_type] || log.template_type}
                                                    </span>
                                                    <span>{log.sent_at ? new Date(log.sent_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</span>
                                                    <span>· {log.recipient_email}</span>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Buchungs-Link + Aktionen */}
                        <div className="flex items-center justify-between gap-2 pt-2 border-t">
                            <a
                                href={`/admin/participants/${bookingId}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                                data-testid="waiting-detail-open-participant"
                            >
                                Buchungs-Detail öffnen
                                <ArrowLeft className="h-3 w-3 rotate-180" />
                            </a>
                            <div className="flex items-center gap-2">
                                {!isPending && (
                                    <Button size="sm" variant="outline" onClick={() => onPromote(bookingId)} data-testid="waiting-detail-promote">
                                        <ArrowUpCircle className="h-3.5 w-3.5 mr-1" /> Promoten
                                    </Button>
                                )}
                                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onRemove(bookingId)} data-testid="waiting-detail-remove">
                                    <Trash className="h-3.5 w-3.5 mr-1" /> Entfernen
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
